import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { useScrollIntoViewOn } from "../components/useScrollIntoViewOn";
import { formatRelative } from "../utils/datetime";
import ControllerActionModal, {
  ActionMeta,
  ActionsInfo,
} from "../components/ControllerActionModal";
import { SortableTh, sortRows, useSort } from "../components/SortableTh";
import { useAuth } from "../context/AuthContext";
import { useCustomer } from "../context/CustomerContext";
import { usePublishScreenData } from "../utils/aiScreenContext";
import ActionButton from "../components/ActionButton";
import { formatDate } from "../utils/datetime";

// Meraki cloud-controller hub: browse a customer's Meraki org → networks →
// devices / clients / wireless / VLANs / firewall, plus license expirations.
// Reads proxy the Meraki Dashboard API via /api/v1/meraki/*. All CHANGES go
// through the ✨ AI Assist at the top of the page (scope=meraki), which
// proposes approval-gated actions — this page publishes the current
// integration/network context so the assistant can act with real identifiers.

type Integration = {
  id: number;
  name: string;
  customer_id: number | null;
  vendor: string;
  vendor_label?: string;
  capabilities?: string[];
  writes_enabled?: boolean;
  organization_id?: string | null;
  enabled: boolean;
  last_sync_status?: string | null;
  last_sync_at?: string | null;
};
type VendorInfo = { vendor: string; label: string; capabilities?: string[] };
type Network = { id: string; name: string; product_types: string[] };
type Device = {
  hostname?: string | null;
  model?: string;
  serial?: string;
  status?: string;
  ip_address?: string;
  firmware?: string;
};
type Client = {
  id?: string;
  description?: string;
  ip?: string;
  mac?: string;
  vlan?: number;
  status?: string;
  manufacturer?: string;
  ssid?: string;
};
type Ssid = {
  number: number;
  name?: string;
  enabled?: boolean;
  authMode?: string;
  visible?: boolean;
  ipAssignmentMode?: string;
  useVlanTagging?: boolean;
  defaultVlanId?: number;
  bandSelection?: string;
  perClientBandwidthLimitUp?: number;
  perClientBandwidthLimitDown?: number;
};
type SiteToSite = {
  mode?: string;
  subnets?: { localSubnet?: string; useVpn?: boolean }[];
  hubs?: { hubId?: string; useDefaultRoute?: boolean }[];
};
type IpsecPolicies = {
  ikeCipherAlgo?: string[];
  ikeAuthAlgo?: string[];
  ikeDiffieHellmanGroup?: string[];
  ikeLifetime?: number;
  ikePrfAlgo?: string[];
  childCipherAlgo?: string[];
  childAuthAlgo?: string[];
  childPfsGroup?: string[];
  childLifetime?: number;
};
type IpsecPeer = {
  name?: string;
  publicIp?: string;
  privateSubnets?: string[];
  secret?: string;
  ikeVersion?: string;
  ipsecPoliciesPreset?: string;
  ipsecPolicies?: IpsecPolicies;
  networkTags?: string[];
};
type IpsecRow = {
  name: string;
  publicIp: string;
  privateSubnets: string;
  secret: string;
  ikeVersion: string;
  preset: string;
  policyMode: "preset" | "custom";
  // custom Phase 1 (IKE) + Phase 2 (child/ESP), algo lists as CSV
  ikeCipher: string;
  ikeAuth: string;
  ikeDH: string;
  ikeLifetime: string;
  ikePrf: string;
  childCipher: string;
  childAuth: string;
  childPfs: string;
  childLifetime: string;
};
type VpnUser = {
  id?: string;
  email?: string;
  name?: string;
  accountType?: string;
  isAdmin?: boolean;
  enabled?: boolean;
};
type GroupPolicy = {
  groupPolicyId?: string;
  name?: string;
  splashAuthSettings?: string;
  limitUp?: number | null;
  limitDown?: number | null;
  l3FirewallRuleCount?: number;
};
type ClientVpnInfo = {
  deploymentMode?: string;
  dynamicDnsEnabled?: boolean;
  dynamicDnsHostname?: string;
};
type StaticRoute = {
  id?: string;
  name?: string;
  subnet?: string;
  gatewayIp?: string;
  enabled?: boolean;
};
type ApplPort = {
  number?: number;
  enabled?: boolean;
  type?: string;
  vlan?: number;
  allowedVlans?: string;
  dropUntaggedTraffic?: boolean;
};
type NatOneToOne = {
  name?: string;
  publicIp?: string;
  lanIp?: string;
  uplink?: string;
  allowedInbound?: { protocol?: string; destinationPorts?: string[]; allowedIps?: string[] }[];
};
type NatOneToMany = {
  publicIp?: string;
  uplink?: string;
  portRules?: {
    name?: string;
    protocol?: string;
    publicPort?: string;
    localIp?: string;
    localPort?: string;
    allowedIps?: string[];
  }[];
};
type Nat1to1Row = {
  name: string;
  publicIp: string;
  lanIp: string;
  uplink: string;
  protocol: string;
  ports: string;
  allowedIps: string;
};
type Nat1manyRow = {
  publicIp: string;
  uplink: string;
  name: string;
  protocol: string;
  publicPort: string;
  localIp: string;
  localPort: string;
  allowedIps: string;
};
type Vlan = {
  id?: string | number;
  name?: string;
  subnet?: string;
  applianceIp?: string;
  dhcpHandling?: string;
};
type FwRule = {
  comment?: string;
  policy?: string;
  protocol?: string;
  srcCidr?: string;
  srcPort?: string;
  destCidr?: string;
  destPort?: string;
  syslogEnabled?: boolean;
};

// Meraki auto-applies a trailing default allow-any rule; it's returned by the
// GET but must NOT be sent on the PUT. We hide it from the editor.
const isDefaultRule = (r: FwRule) => (r.comment || "").toLowerCase() === "default rule";

// Sort value extractor for the shared sortRows helper — numbers sort
// numerically, everything else case-insensitively.
const cmp = (r: Record<string, unknown>, k: string): string | number => {
  const v = r[k];
  if (v == null) return "";
  if (typeof v === "number" || typeof v === "boolean") return Number(v);
  const n = Number(v);
  return v !== "" && !Number.isNaN(n) ? n : String(v).toLowerCase();
};
type License = {
  license_id: string;
  edition?: string | null;
  device_serial?: string | null;
  state?: string | null;
  expiration_date?: string | null;
  days_until_expiry?: number | null;
};

type PortForward = {
  name?: string;
  protocol?: string;
  publicPort?: string;
  lanIp?: string; // Meraki's field for the internal target host
  localPort?: string;
  uplink?: string;
  allowedIps?: string[];
};
type SwitchPort = {
  portId?: string;
  name?: string;
  enabled?: boolean;
  type?: string;
  vlan?: number;
  voiceVlan?: number;
  allowedVlans?: string;
  poeEnabled?: boolean;
};

type LicenseOverview = {
  model?: string | null;
  expirationDate?: string | null;
  status?: string | null;
  licensedDeviceCounts?: Record<string, number>;
  licenses?: {
    id?: string;
    key?: string;
    editions?: (string | null)[];
    counts?: { model?: string; count?: number }[];
    expired?: boolean;
    mode?: string;
    durationInDays?: number;
    device_serial?: string | null;
    state?: string | null;
    expirationDate?: string | null;
  }[];
};

type ProposedAction = {
  id: number;
  action_type: string;
  params: Record<string, unknown>;
  risk_tier: string;
  status: string;
};

const TABS = [
  "Devices",
  "Clients",
  "VPN",
  "VPN Users",
  "Wireless",
  "VLANs",
  "Firewall",
  "Routing",
  "Group Policies",
  "Licenses",
] as const;
type Tab = (typeof TABS)[number];

// Anything healthy/OK lights up green; problems red; unknown gray.
const OK_STATES = new Set(["online", "active", "ok", "healthy", "up", "connected", "enabled", "ready", "running"]);
const BAD_STATES = new Set(["alerting", "offline", "dormant", "down", "disconnected", "expired", "failed", "disabled"]);
function statusPill(s?: string) {
  const v = (s || "").toLowerCase();
  const cls = OK_STATES.has(v)
    ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
    : BAD_STATES.has(v)
      ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"
      : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";
  return <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{s || "unknown"}</span>;
}

// Days-until-expiry coloring mirrors the monitoring thresholds (warn 30d,
// critical 7d, expired).
function expiryCell(days?: number | null, date?: string | null) {
  const d = date ? new Date(date).toLocaleDateString() : "—";
  if (days == null) return <span className="text-gray-500">{d}</span>;
  const cls =
    days < 0
      ? "text-red-700 dark:text-red-400 font-semibold"
      : days <= 7
        ? "text-red-600 dark:text-red-400 font-semibold"
        : days <= 30
          ? "text-amber-600 dark:text-amber-400 font-medium"
          : "text-gray-700 dark:text-gray-200";
  const rel = days < 0 ? `expired ${-days}d ago` : `in ${days}d`;
  return (
    <span className={cls}>
      {d} <span className="text-xs font-normal opacity-80">({rel})</span>
    </span>
  );
}

/**
 * On / Off / "—".
 *
 * The third case is the point. A row that did not report a field is not a
 * row whose field is false: rendering `undefined` as "Off" would say a
 * firewall rule is disabled when all we know is that we did not read it.
 */
function onOff(v: unknown) {
  if (v === null || v === undefined || v === "") return <span className="text-gray-400">—</span>;
  return v ? "On" : "Off";
}

/** An expiry that may arrive as a unix timestamp or as an ISO string. */
function epochOrDate(v: unknown) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number" || /^\d+$/.test(String(v))) {
    const ms = Number(v) * 1000;
    if (!Number.isFinite(ms)) return String(v);
    return new Date(ms).toLocaleDateString();
  }
  // parseUtc, not new Date(str): the API's timestamps are zone-less UTC.
  return formatDate(String(v));
}

// Tab -> the read capability it maps to. Module scope so the edit-button
// placement can consult it before the component body has run.
// Every capability is served at /cloud-controllers/{id}/<capability>, so the
// path IS the capability name. The lookup stays because two of them read
// better as a tab than as an API noun.
const GEN_CAP_PATH_LOOKUP: Record<string, string> = {
  devices: "devices",
  sites: "sites",
  clients: "clients",
  ssids: "ssids",
  vlans: "vlans",
};

/**
 * Tab label -> capability, in the order the tabs appear.
 *
 * This is the ONLY list. The tab bar, the fetch, the column set, the
 * "nothing collected" message and the Edit/Add buttons all read it, so a
 * capability cannot be added to an adapter and then show up in four places
 * and not the fifth.
 *
 * The labels are ours, not any vendor's: a controller declares
 * `firewall_l3` and the operator reads "Firewall rules".
 */
const GEN_TAB_CAP_LOOKUP: Record<string, string> = {
  Devices: "devices",
  Sites: "sites",
  Clients: "clients",
  Wireless: "ssids",
  VLANs: "vlans",
  Interfaces: "appliance_ports",
  "Switch ports": "switch_ports",
  "Firewall rules": "firewall_l3",
  Addresses: "address_objects",
  Services: "service_objects",
  "Port forwards": "port_forwarding",
  "NAT pools": "nat",
  Routing: "static_routes",
  DHCP: "dhcp_servers",
  "Site-to-site VPN": "vpn_ipsec",
  "Remote access": "client_vpn",
  "VPN users": "vpn_users",
  Administrators: "admins",
  Licensing: "licensing",
};

// Column defs for the vendor-agnostic read tabs (non-Meraki controllers).
type GenCol = { key: string; label: string; mono?: boolean; render?: (r: any) => React.ReactNode };
const GEN_COLUMNS: Record<string, GenCol[]> = {
  // Site health + the ISP picture. Deliberately rows rather than summary
  // cards: the capability-driven grid below already sorts, so this needed no
  // new UI - and on a multi-site account the comparison IS the value.
  //
  // Offline / pending-updates / critical read as "0" when healthy, which is
  // the point; an empty cell would be indistinguishable from "not reported".
  Sites: [
    { key: "site", label: "Site" },
    { key: "total_devices", label: "Devices" },
    { key: "offline_devices", label: "Offline" },
    { key: "pending_updates", label: "Updates due" },
    { key: "critical_notifications", label: "Critical" },
    { key: "wifi_clients", label: "WiFi clients" },
    { key: "wired_clients", label: "Wired clients" },
    { key: "isp", label: "ISP", render: (r) => r.isp || "—" },
    { key: "external_ip", label: "WAN IP", mono: true },
    {
      key: "wan_uptime",
      label: "WAN uptime",
      render: (r) => (r.wan_uptime == null ? "—" : `${r.wan_uptime}%`),
    },
    {
      key: "latency_max_ms",
      label: "Peak latency",
      // Max, not average: the call an MSP fields is "it went bad for a
      // minute", and an average hides exactly that.
      render: (r) => (r.latency_max_ms == null ? "—" : `${r.latency_max_ms} ms`),
    },
  ],
  Devices: [
    { key: "hostname", label: "Name", render: (r) => r.hostname || "—" },
    { key: "model", label: "Model" },
    { key: "serial", label: "Serial", mono: true },
    { key: "ip_address", label: "IP" },
    { key: "mac_address", label: "MAC", mono: true },
    { key: "firmware", label: "Firmware" },
    { key: "status", label: "Status", render: (r) => statusPill(r.status) },
  ],
  Clients: [
    { key: "description", label: "Client", render: (r) => r.description || "—" },
    { key: "ip", label: "IP" },
    { key: "mac", label: "MAC", mono: true },
    { key: "vlan", label: "VLAN" },
    { key: "ssid", label: "SSID" },
    { key: "manufacturer", label: "Manufacturer" },
    { key: "status", label: "Status", render: (r) => statusPill(r.status) },
  ],
  Wireless: [
    { key: "number", label: "#" },
    { key: "name", label: "SSID" },
    { key: "enabled", label: "Enabled", render: (r) => (r.enabled ? "On" : "Off") },
    { key: "authMode", label: "Auth" },
    { key: "visible", label: "Broadcast", render: (r) => (r.visible === false ? "hidden" : "visible") },
  ],
  VLANs: [
    { key: "id", label: "VLAN" },
    { key: "name", label: "Name" },
    { key: "subnet", label: "Subnet", mono: true },
    { key: "purpose", label: "Purpose" },
    { key: "dhcpHandling", label: "DHCP" },
  ],

  // ── the gateway/firewall tabs ──────────────────────────────────────────
  // A boolean ALWAYS gets a render. Without one the default stringifies to
  // "true"/"false" in a table of otherwise readable values.
  Interfaces: [
    { key: "name", label: "Interface" },
    { key: "alias", label: "Alias" },
    { key: "enabled", label: "Up", render: (r) => onOff(r.enabled) },
    { key: "type", label: "Type" },
    { key: "role", label: "Role" },
    { key: "address", label: "Address", mono: true },
    { key: "addressing", label: "Addressing" },
    { key: "vlan_id", label: "VLAN" },
    { key: "parent", label: "On" },
    { key: "management_access", label: "Management access" },
  ],
  "Switch ports": [
    { key: "name", label: "Port" },
    { key: "enabled", label: "Enabled", render: (r) => onOff(r.enabled) },
    { key: "vlan", label: "VLAN" },
    { key: "type", label: "Type" },
    { key: "status", label: "Status", render: (r) => statusPill(r.status as string) },
  ],
  "Firewall rules": [
    { key: "id", label: "#" },
    { key: "name", label: "Rule" },
    { key: "enabled", label: "Enabled", render: (r) => onOff(r.enabled) },
    { key: "action", label: "Action" },
    { key: "source_interfaces", label: "From" },
    { key: "sources", label: "Source" },
    { key: "destination_interfaces", label: "To" },
    { key: "destinations", label: "Destination" },
    { key: "services", label: "Service" },
    { key: "nat", label: "NAT", render: (r) => onOff(r.nat) },
    { key: "logging", label: "Logging" },
    { key: "comment", label: "Comment" },
  ],
  Addresses: [
    { key: "name", label: "Name" },
    { key: "kind", label: "Kind" },
    { key: "value", label: "Value", mono: true },
    { key: "interface", label: "Interface" },
    { key: "comment", label: "Comment" },
  ],
  Services: [
    { key: "name", label: "Service" },
    { key: "protocol", label: "Protocol" },
    { key: "tcp_ports", label: "TCP ports", mono: true },
    { key: "udp_ports", label: "UDP ports", mono: true },
    { key: "category", label: "Category" },
  ],
  "Port forwards": [
    { key: "name", label: "Name" },
    { key: "interface", label: "On" },
    { key: "external_ip", label: "Outside", mono: true },
    { key: "external_port", label: "Outside port", mono: true },
    { key: "protocol", label: "Protocol" },
    { key: "internal_ip", label: "Inside", mono: true },
    { key: "internal_port", label: "Inside port", mono: true },
    { key: "port_forward", label: "Forwarding", render: (r) => onOff(r.port_forward) },
  ],
  "NAT pools": [
    { key: "name", label: "Pool" },
    { key: "kind", label: "Kind" },
    { key: "range", label: "Addresses", mono: true },
    { key: "source_range", label: "Applies to", mono: true },
    { key: "comment", label: "Comment" },
  ],
  Routing: [
    { key: "destination", label: "Destination", mono: true },
    { key: "gateway", label: "Gateway", mono: true },
    { key: "interface", label: "Interface" },
    { key: "distance", label: "Distance" },
    { key: "priority", label: "Priority" },
    { key: "enabled", label: "Enabled", render: (r) => onOff(r.enabled) },
    { key: "comment", label: "Comment" },
  ],
  DHCP: [
    { key: "interface", label: "Interface" },
    { key: "enabled", label: "Enabled", render: (r) => onOff(r.enabled) },
    { key: "range_start", label: "From", mono: true },
    { key: "range_end", label: "To", mono: true },
    { key: "netmask", label: "Mask", mono: true },
    { key: "gateway", label: "Gateway", mono: true },
    { key: "dns", label: "DNS", mono: true },
    {
      key: "lease_seconds",
      label: "Lease",
      // Seconds are how the API says it and not how anyone reads it.
      render: (r) =>
        r.lease_seconds == null ? "—" : `${Math.round(Number(r.lease_seconds) / 3600)} h`,
    },
    { key: "domain", label: "Domain" },
  ],
  "Site-to-site VPN": [
    { key: "name", label: "Tunnel" },
    { key: "interface", label: "Interface" },
    { key: "remote_gateway", label: "Remote gateway", mono: true },
    { key: "mode", label: "Mode" },
    { key: "ike_version", label: "IKE" },
    { key: "authentication", label: "Authentication" },
    { key: "comment", label: "Comment" },
  ],
  "Remote access": [
    { key: "name", label: "Service" },
    { key: "enabled", label: "Enabled", render: (r) => onOff(r.enabled) },
    { key: "port", label: "Port", mono: true },
    { key: "listen_on", label: "Listens on" },
    { key: "allowed_sources", label: "Allowed from" },
    { key: "address_pools", label: "Address pool" },
    { key: "dns", label: "DNS", mono: true },
    { key: "certificate", label: "Certificate" },
  ],
  "VPN users": [
    { key: "name", label: "User" },
    { key: "enabled", label: "Enabled", render: (r) => onOff(r.enabled) },
    { key: "kind", label: "Kind" },
    { key: "email", label: "Email" },
    { key: "two_factor", label: "Two-factor" },
  ],
  Administrators: [
    { key: "name", label: "Account" },
    { key: "profile", label: "Access profile" },
    // "any address" is the finding, not a blank cell - see the adapter.
    { key: "trusted_hosts", label: "Restricted to" },
    { key: "two_factor", label: "Two-factor" },
    { key: "comment", label: "Comment" },
  ],
  Licensing: [
    { key: "name", label: "Subscription" },
    { key: "status", label: "Status", render: (r) => statusPill(r.status as string) },
    { key: "expires", label: "Expires", render: (r) => epochOrDate(r.expires) },
    { key: "version", label: "Version" },
  ],
};

export default function MerakiController() {
  const { currentCustomerId, current } = useCustomer();
  const { user } = useAuth();
  const canManageRole = user?.role === "admin" || user?.role === "technician";
  const [aiOn, setAiOn] = useState(false);
  useEffect(() => {
    api
      .get("/api/v1/ai/enabled")
      .then((r) => setAiOn(!!r.data?.enabled))
      .catch(() => setAiOn(false));
  }, []);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [vendorList, setVendorList] = useState<VendorInfo[]>([]);
  const [genTab, setGenTab] = useState("Devices");
  const [genRows, setGenRows] = useState<Record<string, unknown>[]>([]);
  // What this controller declares it can change. Fetched per integration so
  // the edit buttons reflect the ADAPTER, not an assumption in this file.
  const [ccActions, setCcActions] = useState<ActionsInfo | null>(null);
  // Where a relayed controller's rows came from, and when.
  const [freshness, setFreshness] = useState<{
    live: boolean;
    relayed: boolean;
    fetched_at?: string | null;
    collector?: string | null;
    last_sync_status?: string | null;
    last_sync_error?: string | null;
    // Present only for capabilities the agent actually collected. A MISSING
    // key is not the same as row_count 0 - see the empty state below.
    capabilities?: Record<string, { fetched_at: string | null; row_count: number }>;
  } | null>(null);
  const [editing, setEditing] = useState<{
    action: ActionMeta;
    objectId: string;
    objectLabel: string;
    initial: Record<string, unknown>;
  } | null>(null);
  // Objects that the open editor's reference fields can point at.
  const [refOptions, setRefOptions] = useState<
    Record<string, { value: string; label: string }[]>
  >({});
  const [genLoading, setGenLoading] = useState(false);
  const [niId, setNiId] = useState<number | null>(null);
  const [networks, setNetworks] = useState<Network[]>([]);
  const [netId, setNetId] = useState<string>("");
  const [tab, setTab] = useState<Tab>("Devices");

  const [devices, setDevices] = useState<Device[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [ssids, setSsids] = useState<Ssid[]>([]);
  const [vlans, setVlans] = useState<Vlan[]>([]);
  const [fw, setFw] = useState<FwRule[]>([]);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [licenseOverview, setLicenseOverview] = useState<LicenseOverview | null>(null);
  const [claimKey, setClaimKey] = useState("");
  const [claimMode, setClaimMode] = useState("addDevices");
  const [moveLicId, setMoveLicId] = useState("");
  const [moveDest, setMoveDest] = useState("");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Operator-initiated changes → approval-gated AIActions shown in the strip.
  const [pending, setPending] = useState<ProposedAction[]>([]);
  const [modalAction, setModalAction] = useState<ProposedAction | null>(null);
  const [proposeErr, setProposeErr] = useState("");
  const [editVlan, setEditVlan] = useState<{
    id: string;
    name: string;
    subnet: string;
    applianceIp: string;
    isNew?: boolean;
  } | null>(null);
  const [editSsid, setEditSsid] = useState<{
    number: number;
    name: string;
    enabled: boolean;
    authMode: string;
    psk: string;
    visible: boolean;
    ipAssignmentMode: string;
    useVlanTagging: boolean;
    defaultVlanId: string;
    bandSelection: string;
    bwUp: string;
    bwDown: string;
  } | null>(null);
  const [applPorts, setApplPorts] = useState<ApplPort[]>([]);
  const [editApplPort, setEditApplPort] = useState<{
    port_id: string;
    enabled: boolean;
    type: string;
    vlan: string;
    allowedVlans: string;
    dropUntaggedTraffic: boolean;
  } | null>(null);
  const [nat, setNat] = useState<{ oneToOne: NatOneToOne[]; oneToMany: NatOneToMany[] }>({
    oneToOne: [],
    oneToMany: [],
  });
  const [s2s, setS2s] = useState<SiteToSite | null>(null);
  const [s2sEdit, setS2sEdit] = useState<{
    mode: string;
    subnets: { localSubnet: string; useVpn: boolean }[];
    hubs: { hubId: string; useDefaultRoute: boolean }[];
  } | null>(null);
  const [ipsec, setIpsec] = useState<IpsecPeer[]>([]);
  const [ipsecEdit, setIpsecEdit] = useState<IpsecRow[] | null>(null);
  const [clientVpn, setClientVpn] = useState<ClientVpnInfo | null>(null);
  const [groupPolicies, setGroupPolicies] = useState<GroupPolicy[]>([]);
  // EVERY inline editor on this page renders above the table it edits, so
  // on a controller with a real device list the editor opened off-screen and
  // the Edit click looked inert. Reported as "Edit does nothing".
  //
  // One ref serves all of them: they are per-tab and mutually exclusive, so
  // only one is ever mounted at a time.
  const editorRef = useRef<HTMLDivElement>(null);

  const [editDevice, setEditDevice] = useState<{
    serial: string;
    name: string;
    address: string;
    notes: string;
    tags: string;
  } | null>(null);
  const [vpnUsers, setVpnUsers] = useState<VpnUser[]>([]);
  const [pwUser, setPwUser] = useState<string | null>(null);
  const [pwValue, setPwValue] = useState("");
  const [pwEmail, setPwEmail] = useState(false);
  const [vpnMsg, setVpnMsg] = useState("");
  const [vpnAdd, setVpnAdd] = useState<{
    email: string;
    name: string;
    password: string;
    emailToUser: boolean;
  } | null>(null);
  const [routes, setRoutes] = useState<StaticRoute[]>([]);
  const [editRoute, setEditRoute] = useState<{
    id?: string;
    name: string;
    subnet: string;
    gatewayIp: string;
    enabled: boolean;
  } | null>(null);
  const [nat1to1Edit, setNat1to1Edit] = useState<Nat1to1Row[] | null>(null);
  const [nat1manyEdit, setNat1manyEdit] = useState<Nat1manyRow[] | null>(null);
  // clientId/mac → current access policy (Blocked/Allowed/Normal/group). Clients
  // absent from the API list default to Normal.
  const [clientPolicies, setClientPolicies] = useState<Record<string, string>>({});
  const [fwEdit, setFwEdit] = useState<FwRule[] | null>(null);
  const [pf, setPf] = useState<PortForward[]>([]);
  const [editPf, setEditPf] = useState<{
    name: string;
    protocol: string;
    publicPort: string;
    localIp: string;
    localPort: string;
    uplink: string;
  } | null>(null);
  const [switchSerial, setSwitchSerial] = useState("");
  const [switchPorts, setSwitchPorts] = useState<SwitchPort[]>([]);
  const [editPort, setEditPort] = useState<{
    port_id: string;
    name: string;
    enabled: boolean;
    type: string;
    vlan: string;
    voiceVlan: string;
    allowedVlans: string;
  } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [vNonce, setVNonce] = useState(0); // bumped after a successful action → refetch to VALIDATE

  const [newNet, setNewNet] = useState<{
    name: string;
    productTypes: string[];
    timezone: string;
  } | null>(null);
  const NET_PRODUCT_TYPES = [
    "appliance",
    "switch",
    "wireless",
    "camera",
    "cellularGateway",
    "sensor",
    "systemsManager",
  ];
  const createNetwork = async () => {
    if (niId == null || !newNet || !newNet.name.trim() || !newNet.productTypes.length) return;
    setProposeErr("");
    try {
      const r = await api.post(`/api/v1/meraki/${niId}/networks`, {
        name: newNet.name.trim(),
        product_types: newNet.productTypes,
        timezone: newNet.timezone || undefined,
      });
      const nets = ((await api.get(`/api/v1/meraki/${niId}/networks`)).data || []) as Network[];
      setNetworks(nets);
      if (r.data?.id) setNetId(r.data.id);
      setNewNet(null);
    } catch (e: any) {
      setProposeErr(e.response?.data?.detail || "Failed to create the network");
    }
  };

  const toggleWrites = async () => {
    if (niId == null) return;
    setErr("");
    try {
      await api.put(`/api/v1/cloud-controllers/${niId}/writes-enabled`, { enabled: !writesOn });
      const list = ((await api.get("/api/v1/cloud-controllers")).data || []) as Integration[];
      setIntegrations(list);
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Failed to update the writes setting");
    }
  };

  const syncNow = async () => {
    if (niId == null) return;
    setSyncing(true);
    setSyncMsg("");
    try {
      await api.post(`/api/v1/meraki/${niId}/sync`);
      setSyncMsg("Synced just now.");
      setReloadKey((k) => k + 1); // re-fetch networks/devices/licenses + active tab
    } catch (e: any) {
      setSyncMsg(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const propose = async (action_type: string, params: Record<string, unknown>) => {
    if (niId == null) return;
    setProposeErr("");
    try {
      const r = await api.post(`/api/v1/meraki/${niId}/propose`, { action_type, params });
      const act = r.data as ProposedAction;
      setPending((prev) => [act, ...prev]);
      setModalAction(act); // pop the approve/reject prompt right where they are
    } catch (e: any) {
      setProposeErr(e.response?.data?.detail || "Failed to propose the change");
    }
  };
  const patchPending = (id: number, u: Partial<ProposedAction>) =>
    setPending((prev) => prev.map((a) => (a.id === id ? { ...a, ...u } : a)));
  // A clicked AI suggestion produced proposed action(s) → log + pop the modal.
  const handleProposed = (acts: ProposedAction[]) => {
    setPending((p) => [...acts, ...p]);
    if (acts[0]) setModalAction(acts[0]);
  };
  // Generic (any-vendor) write → approval-gated cc_write via /cloud-controllers.
  const ccPropose = async (method: string, args: Record<string, unknown>, label: string) => {
    if (niId == null) return;
    setProposeErr("");
    try {
      const r = await api.post(`/api/v1/cloud-controllers/${niId}/propose`, { method, args, label });
      handleProposed([r.data as ProposedAction]);
    } catch (e: any) {
      setProposeErr(e.response?.data?.detail || "Failed to propose the change");
    }
  };

  // Which editor is open, as one value. Changing it scrolls; editing
  // inside an already-open panel does not.
  const activeEditor =
    (editDevice && `device-${editDevice.serial}`) ||
    (editSsid && `ssid-${JSON.stringify(editSsid).slice(0, 40)}`) ||
    (editVlan && `vlan-${JSON.stringify(editVlan).slice(0, 40)}`) ||
    (editApplPort && `applport-${JSON.stringify(editApplPort).slice(0, 40)}`) ||
    (editPort && `port-${JSON.stringify(editPort).slice(0, 40)}`) ||
    (editRoute && `route-${JSON.stringify(editRoute).slice(0, 40)}`) ||
    null;
  useScrollIntoViewOn(editorRef, activeEditor);

  // Reset the inline editors whenever the integration / network / tab changes.
  useEffect(() => {
    setEditVlan(null);
    setEditSsid(null);
    setFwEdit(null);
    setEditPf(null);
    setEditPort(null);
    setEditApplPort(null);
    setEditRoute(null);
    setNat1to1Edit(null);
    setNat1manyEdit(null);
    setPwUser(null);
    setVpnMsg("");
    setVpnAdd(null);
    setS2sEdit(null);
    setIpsecEdit(null);
    setEditDevice(null);
  }, [niId, netId, tab]);

  const proposeDevice = () => {
    if (!editDevice) return;
    const p: Record<string, unknown> = { serial: editDevice.serial };
    if (editDevice.name) p.name = editDevice.name;
    if (editDevice.address) p.address = editDevice.address;
    if (editDevice.notes) p.notes = editDevice.notes;
    if (editDevice.tags.trim()) p.tags = editDevice.tags.split(/[,\s]+/).filter(Boolean);
    propose("meraki_update_device", p);
    setEditDevice(null);
  };
  const deleteVpnUser = async (u: VpnUser) => {
    if (niId == null || !u.id) return;
    if (!window.confirm(`Delete VPN user ${u.email}? This removes their Client VPN access.`)) return;
    setVpnMsg("");
    try {
      await api.delete(`/api/v1/meraki/${niId}/networks/${netId}/vpn-users/${u.id}`);
      loadVpnUsers();
      setVpnMsg("User deleted.");
    } catch (e: any) {
      setVpnMsg(e.response?.data?.detail || "Failed to delete the user");
    }
  };

  // VPN tab helpers (site-to-site AutoVPN + third-party IPSec).
  const startS2sEdit = () =>
    setS2sEdit({
      mode: s2s?.mode || "none",
      subnets: (s2s?.subnets || []).map((x) => ({
        localSubnet: x.localSubnet || "",
        useVpn: !!x.useVpn,
      })),
      hubs: (s2s?.hubs || []).map((h) => ({
        hubId: h.hubId || "",
        useDefaultRoute: !!h.useDefaultRoute,
      })),
    });
  const proposeS2s = () => {
    if (!s2sEdit) return;
    const params: Record<string, unknown> = {
      network_id: netId,
      mode: s2sEdit.mode,
      subnets: s2sEdit.subnets.map((x) => ({ localSubnet: x.localSubnet, useVpn: x.useVpn })),
    };
    if (s2sEdit.mode === "spoke") {
      params.hubs = s2sEdit.hubs
        .filter((h) => h.hubId.trim())
        .map((h) => ({ hubId: h.hubId.trim(), useDefaultRoute: h.useDefaultRoute }));
    }
    propose("meraki_set_site_to_site_vpn", params);
    setS2sEdit(null);
  };
  const _csv = (a?: string[]) => (a || []).join(",");
  const _arr = (s: string) =>
    s
      ? s
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean)
      : [];
  const peerToRow = (p: IpsecPeer): IpsecRow => {
    const pol = p.ipsecPolicies || {};
    const preset = p.ipsecPoliciesPreset;
    const isCustom = !!p.ipsecPolicies && (!preset || preset === "custom");
    return {
      name: p.name || "",
      publicIp: p.publicIp || "",
      privateSubnets: (p.privateSubnets || []).join(","),
      secret: p.secret || "",
      ikeVersion: p.ikeVersion || "2",
      preset: preset && preset !== "custom" ? preset : "default",
      policyMode: isCustom ? "custom" : "preset",
      ikeCipher: _csv(pol.ikeCipherAlgo),
      ikeAuth: _csv(pol.ikeAuthAlgo),
      ikeDH: _csv(pol.ikeDiffieHellmanGroup),
      ikeLifetime: pol.ikeLifetime != null ? String(pol.ikeLifetime) : "28800",
      ikePrf: _csv(pol.ikePrfAlgo),
      childCipher: _csv(pol.childCipherAlgo),
      childAuth: _csv(pol.childAuthAlgo),
      childPfs: _csv(pol.childPfsGroup),
      childLifetime: pol.childLifetime != null ? String(pol.childLifetime) : "28800",
    };
  };
  const BLANK_IPSEC_ROW: IpsecRow = {
    name: "",
    publicIp: "",
    privateSubnets: "",
    secret: "",
    ikeVersion: "2",
    preset: "default",
    policyMode: "preset",
    ikeCipher: "aes256",
    ikeAuth: "sha256",
    ikeDH: "group14",
    ikeLifetime: "28800",
    ikePrf: "prfsha256",
    childCipher: "aes256",
    childAuth: "sha256",
    childPfs: "group14",
    childLifetime: "28800",
  };
  const startIpsecEdit = () => setIpsecEdit((ipsec || []).map(peerToRow));
  // One-click add of a non-Meraki IPSec site-to-site tunnel (enters edit + a new row).
  const addIpsecTunnel = () =>
    setIpsecEdit([...(ipsecEdit || (ipsec || []).map(peerToRow)), { ...BLANK_IPSEC_ROW }]);
  const updIpsec = (i: number, patch: Partial<IpsecRow>) =>
    setIpsecEdit((p) => (p || []).map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const proposeIpsec = () => {
    const peers = (ipsecEdit || []).map((r) => {
      const base: Record<string, unknown> = {
        name: r.name,
        publicIp: r.publicIp,
        privateSubnets: _arr(r.privateSubnets),
        secret: r.secret,
        ikeVersion: r.ikeVersion,
        networkTags: ["all"],
      };
      if (r.policyMode === "custom") {
        base.ipsecPolicies = {
          ikeCipherAlgo: _arr(r.ikeCipher),
          ikeAuthAlgo: _arr(r.ikeAuth),
          ikeDiffieHellmanGroup: _arr(r.ikeDH),
          ikeLifetime: Number(r.ikeLifetime) || 28800,
          ...(r.ikePrf ? { ikePrfAlgo: _arr(r.ikePrf) } : {}),
          childCipherAlgo: _arr(r.childCipher),
          childAuthAlgo: _arr(r.childAuth),
          childPfsGroup: _arr(r.childPfs),
          childLifetime: Number(r.childLifetime) || 28800,
        };
      } else {
        base.ipsecPoliciesPreset = r.preset;
      }
      return base;
    });
    propose("meraki_set_third_party_vpn_peers", { peers });
    setIpsecEdit(null);
  };

  // VPN (Meraki auth) users — direct admin actions (not approval-gated).
  const loadVpnUsers = () => {
    if (niId == null || !netId) return;
    api
      .get(`/api/v1/meraki/${niId}/networks/${netId}/vpn-users`)
      .then((r) => setVpnUsers(r.data || []))
      .catch(() => setVpnUsers([]));
  };
  const setVpnEnabled = async (u: VpnUser, enabled: boolean) => {
    if (niId == null) return;
    setVpnMsg("");
    try {
      await api.post(`/api/v1/meraki/${niId}/networks/${netId}/vpn-users/${u.id}/enable`, { enabled });
      loadVpnUsers();
    } catch (e: any) {
      setVpnMsg(e.response?.data?.detail || "Failed to update the user");
    }
  };
  const resetVpnPw = async () => {
    if (niId == null || !pwUser || !pwValue) return;
    setVpnMsg("");
    try {
      await api.post(`/api/v1/meraki/${niId}/networks/${netId}/vpn-users/${pwUser}/reset-password`, {
        password: pwValue,
        email_to_user: pwEmail,
      });
      setPwUser(null);
      setPwValue("");
      setVpnMsg("Password reset.");
    } catch (e: any) {
      setVpnMsg(e.response?.data?.detail || "Failed to reset the password");
    }
  };
  const createVpnUser = async () => {
    if (niId == null || !vpnAdd || !vpnAdd.email || !vpnAdd.password) return;
    setVpnMsg("");
    try {
      await api.post(`/api/v1/meraki/${niId}/networks/${netId}/vpn-users`, {
        email: vpnAdd.email,
        name: vpnAdd.name,
        password: vpnAdd.password,
        account_type: "Client VPN",
        email_to_user: vpnAdd.emailToUser,
      });
      setVpnAdd(null);
      loadVpnUsers();
      setVpnMsg("User created.");
    } catch (e: any) {
      setVpnMsg(e.response?.data?.detail || "Failed to create the user");
    }
  };
  // "Add SSID" = configure the first disabled/unused SSID slot (Meraki has 15
  // fixed slots; you enable one rather than create it).
  const addSsid = () => {
    const slot = ssids.find((s) => !s.enabled) || ssids[0];
    if (!slot) {
      setProposeErr("No SSID slots available on this network (needs MR access points).");
      return;
    }
    setEditSsid({
      number: slot.number,
      name: slot.name && !/^unconfigured/i.test(slot.name) ? slot.name : "",
      enabled: true,
      authMode: slot.authMode || "open",
      psk: "",
      visible: slot.visible !== false,
      ipAssignmentMode: slot.ipAssignmentMode || "",
      useVlanTagging: !!slot.useVlanTagging,
      defaultVlanId: slot.defaultVlanId != null ? String(slot.defaultVlanId) : "",
      bandSelection: slot.bandSelection || "",
      bwUp: "",
      bwDown: "",
    });
  };

  // MS switches in this org (for the Switch ports section under VLANs).
  const switches = useMemo(
    () => devices.filter((d) => (d.model || "").toUpperCase().startsWith("MS")),
    [devices],
  );
  // Soonest license expiration → days from now (for the ≤30-day warning).
  const licDaysUntil = useMemo(() => {
    const dates: number[] = [];
    const push = (s?: string | null) => {
      if (s) {
        const t = Date.parse(s);
        if (!Number.isNaN(t)) dates.push(t);
      }
    };
    push(licenseOverview?.expirationDate);
    (licenseOverview?.licenses || []).forEach((l) => push(l.expirationDate));
    licenses.forEach((l) => push(l.expiration_date));
    if (!dates.length) return null;
    return Math.floor((Math.min(...dates) - Date.now()) / 86400000);
  }, [licenseOverview, licenses]);

  // Auto-pick the first MS switch when entering the VLANs tab.
  useEffect(() => {
    if (tab === "VLANs" && !switchSerial && switches.length) {
      setSwitchSerial(switches[0].serial || "");
    }
  }, [tab, switches, switchSerial]);

  // Load a switch's ports when one is selected under the VLANs tab.
  useEffect(() => {
    if (niId == null || tab !== "VLANs" || !switchSerial) return;
    setSwitchPorts([]);
    api
      .get(`/api/v1/meraki/${niId}/devices/${switchSerial}/switch-ports`)
      .then((r) => setSwitchPorts(r.data || []))
      .catch(() => setSwitchPorts([]));
  }, [niId, tab, switchSerial, reloadKey]);

  // ── L3 firewall rule editor (full-list replace) ──────────────────────────
  const startFwEdit = () => setFwEdit(fw.filter((r) => !isDefaultRule(r)).map((r) => ({ ...r })));
  const addFwRule = () =>
    setFwEdit((prev) => [
      ...(prev || []),
      { policy: "deny", protocol: "any", srcCidr: "Any", srcPort: "Any", destCidr: "Any", destPort: "Any", comment: "" },
    ]);
  const updateFwRule = (i: number, patch: Partial<FwRule>) =>
    setFwEdit((prev) => (prev || []).map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeFwRule = (i: number) => setFwEdit((prev) => (prev || []).filter((_, idx) => idx !== i));
  const moveFwRule = (i: number, dir: number) =>
    setFwEdit((prev) => {
      if (!prev) return prev;
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const copy = [...prev];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  const proposeFwRules = () => {
    const rules = (fwEdit || []).map((r) => ({
      comment: r.comment || "",
      policy: r.policy || "deny",
      protocol: r.protocol || "any",
      srcCidr: r.srcCidr || "Any",
      srcPort: r.srcPort || "Any",
      destCidr: r.destCidr || "Any",
      destPort: r.destPort || "Any",
      syslogEnabled: !!r.syslogEnabled,
    }));
    propose("meraki_set_l3_firewall_rules", { network_id: netId, rules });
    setFwEdit(null);
  };

  // ── Port forwarding (Firewall tab) ───────────────────────────────────────
  const proposePf = () => {
    if (!editPf) return;
    propose("meraki_set_port_forward", {
      network_id: netId,
      name: editPf.name,
      protocol: editPf.protocol,
      public_port: editPf.publicPort,
      local_ip: editPf.localIp,
      local_port: editPf.localPort,
      uplink: editPf.uplink,
    });
    setEditPf(null);
  };

  // ── Switch ports (VLANs tab) ─────────────────────────────────────────────
  const proposePort = () => {
    if (!editPort || !switchSerial) return;
    const f: Record<string, unknown> = {
      serial: switchSerial,
      port_id: editPort.port_id,
      enabled: editPort.enabled,
      type: editPort.type || undefined,
    };
    if (editPort.name) f.name = editPort.name;
    if (editPort.vlan !== "") f.vlan = Number(editPort.vlan);
    if (editPort.voiceVlan !== "") f.voiceVlan = Number(editPort.voiceVlan);
    if (editPort.type === "trunk" && editPort.allowedVlans) f.allowedVlans = editPort.allowedVlans;
    propose("meraki_update_switchport", f);
    setEditPort(null);
  };

  // ── MX gateway ports (VLANs tab) ─────────────────────────────────────────
  const allowedIncludes = (allowed: string | undefined, vlanId: number): boolean => {
    const a = (allowed || "").toLowerCase();
    if (!a) return false;
    if (a === "all") return true;
    return a.split(",").some((part) => {
      const t = part.trim();
      if (t.includes("-")) {
        const [x, y] = t.split("-").map(Number);
        return vlanId >= x && vlanId <= y;
      }
      return Number(t) === vlanId;
    });
  };
  // Which MX gateway ports carry a given VLAN, and HOW: untagged on an access
  // port ("access") or a trunk's native VLAN ("native"), vs tagged on a trunk
  // ("tagged"). Directly answers "what port is this VLAN on, tagged or not?".
  const gatewayPortsFor = (vlanId: number): { port: number; kind: string }[] => {
    const out: { port: number; kind: string }[] = [];
    for (const p of applPorts) {
      if (p.number == null) continue;
      if (p.type === "trunk") {
        if (Number(p.vlan) === vlanId) out.push({ port: p.number, kind: "native" });
        else if (allowedIncludes(p.allowedVlans, vlanId))
          out.push({ port: p.number, kind: "tagged" });
      } else if (Number(p.vlan) === vlanId) {
        out.push({ port: p.number, kind: "access" });
      }
    }
    return out;
  };
  const proposeApplPort = () => {
    if (!editApplPort) return;
    const f: Record<string, unknown> = {
      network_id: netId,
      port_id: editApplPort.port_id,
      enabled: editApplPort.enabled,
      type: editApplPort.type || undefined,
      dropUntaggedTraffic: editApplPort.dropUntaggedTraffic,
    };
    if (editApplPort.vlan !== "") f.vlan = Number(editApplPort.vlan);
    if (editApplPort.type === "trunk" && editApplPort.allowedVlans)
      f.allowedVlans = editApplPort.allowedVlans;
    propose("meraki_update_appliance_port", f);
    setEditApplPort(null);
  };

  // ── Client block/allow (Clients tab) ─────────────────────────────────────
  const clientPol = (c: Client): string =>
    clientPolicies[c.id || ""] || clientPolicies[c.mac || ""] || "Normal";
  const setClientPolicy = (c: Client, policy: string) =>
    propose("meraki_set_client_policy", {
      network_id: netId,
      client_id: c.id || c.mac,
      device_policy: policy,
    });

  const currentIntegration = useMemo(
    () => integrations.find((i) => i.id === niId) || null,
    [integrations, niId],
  );
  const currentVendor = currentIntegration?.vendor || null;
  const isMeraki = currentVendor === "meraki";
  // Writes require the per-integration gate (Meraki verified → default on; other
  // vendors off until an admin validates + enables). canManage gates all write UI.
  // Resolve the open editor's reference fields. The adapter names a
  // CAPABILITY, never a URL, so this goes through the same agnostic read path
  // as the tabs and stays vendor-neutral.
  useEffect(() => {
    const refs = editing?.action.refs;
    if (!editing || !niId || !refs || Object.keys(refs).length === 0) {
      setRefOptions({});
      return;
    }
    let live = true;
    const wanted = Object.entries(refs);
    Promise.all(
      wanted.map(([field, cap]) =>
        api
          .get(`/api/v1/cloud-controllers/${niId}/${GEN_CAP_PATH_LOOKUP[cap] || cap}`)
          .then((r) => [field, (r.data || []) as Record<string, unknown>[]] as const)
          // A failed lookup leaves the field as a text box rather than an
          // empty dropdown the operator cannot get past.
          .catch(() => [field, [] as Record<string, unknown>[]] as const),
      ),
    ).then((pairs) => {
      if (!live) return;
      const refCap = Object.fromEntries(wanted) as Record<string, string>;
      const out: Record<string, { value: string; label: string }[]> = {};
      for (const [field, rows] of pairs) {
        out[field] = rows
          .map((row) => {
            const value = String(row.object_id ?? row.id ?? "");
            const name = String(row.name ?? value);
            // A VLAN number is how an operator recognises a network, so show
            // it when the row has one. Only on a VLAN list, though: on an
            // address-object list `id` is the object's own name, and this
            // read "Accounting (VLAN Accounting)".
            const vlan =
              refCap[field] === "vlans" && row.id != null && row.id !== ""
                ? ` (VLAN ${row.id})`
                : "";
            return { value, label: `${name}${vlan}` };
          })
          .filter((o) => o.value);
      }
      setRefOptions(out);
    });
    return () => {
      live = false;
    };
  }, [editing, niId]);

  const writesOn = currentIntegration?.writes_enabled ?? isMeraki;

  // Re-read the action surface whenever the controller or its write gate
  // changes, so toggling writes off really does remove the buttons.
  useEffect(() => {
    if (!niId || isMeraki) {
      setCcActions(null);
      return;
    }
    let live = true;
    api
      .get(`/api/v1/cloud-controllers/${niId}/actions`)
      .then((r) => live && setCcActions(r.data as ActionsInfo))
      .catch(() => live && setCcActions(null));
    api
      .get(`/api/v1/cloud-controllers/${niId}/freshness`)
      .then((r) => live && setFreshness(r.data))
      .catch(() => live && setFreshness(null));
    return () => {
      live = false;
    };
  }, [niId, isMeraki, writesOn, vNonce]);
  const canManage = canManageRole && writesOn;
  // Non-Meraki write controls on the generic tabs (Devices reboot / SSID toggle).
  /**
   * Whether this tab gets an Actions column.
   *
   * This was hardcoded to Devices and Wireless - the two tabs that have a
   * built-in quick toggle. Everything else an adapter declared it could
   * change had its Edit button rendered into a column that did not exist, so
   * the action was reachable by API and invisible in the product. Now the
   * adapter decides: a tab has actions if the controller says so.
   */
  // GEN_TAB_CAP_LOOKUP, not the GEN_TAB_CAP alias below it: this runs ~150
  // lines before that alias is initialised, and reading it here throws
  // "Cannot access before initialization" - a blank page, not a warning. The
  // two built-in tabs short-circuit the || first, so the crash only appeared
  // on the tabs that had just been added. A screenshot of the page as it
  // loads looks perfect; only clicking a tab shows it.
  const genWrite =
    canManageRole &&
    writesOn &&
    (genTab === "Devices" ||
      genTab === "Wireless" ||
      (ccActions?.actions || []).some((a) => a.target === GEN_TAB_CAP_LOOKUP[genTab]));
  const netName = useMemo(
    () => networks.find((n) => n.id === netId)?.name || "",
    [networks, netId],
  );

  // Click-to-sort per table. Every read-only grid on the hub sorts; the two
  // kinds of table that DON'T are the rule EDITORS (firewall / NAT), because
  // there the row index is the identity used for reorder + per-row updates and
  // rule order is what the MX actually evaluates. In the read-only firewall
  // view sorting is safe because the "#" column carries the original
  // evaluation position, so it stays visible however you sort (and sorting by
  // "#" puts the list back in MX order).
  const devSort = useSort("hostname");
  const cliSort = useSort("description");
  const ssidSort = useSort("number");
  const vlanSort = useSort("id");
  const licSort = useSort("expiration_date");
  const vpnUserSort = useSort("email");
  const fwSort = useSort("rule");
  const pfSort = useSort("name");
  const nat11Sort = useSort("name");
  const nat1mSort = useSort("publicIp");
  const routeSort = useSort("name");
  const gpSort = useSort("name");
  /**
   * Sorting for the capability-driven grid.
   *
   * Every other table on this page has had click-to-sort since #40; this one
   * never did, which did not show while it was five columns of DHCP leases
   * and became the difference between usable and not at 46 licence rows.
   *
   * The key starts empty and resets when the tab changes: an empty key makes
   * cmp() return "" for every row, and sortRows is a stable decorate-sort,
   * so the grid keeps the controller's own order until somebody asks for
   * something else. Carrying a sort across tabs would silently reorder a
   * table by a column it does not have.
   */
  const genSort = useSort("");
  const genSetSort = genSort.setSort;
  useEffect(() => {
    genSetSort({ key: "", dir: "asc" });
  }, [genTab, genSetSort]);
  const sortedGenRows = useMemo(
    () => sortRows(genRows, genSort.sort, cmp),
    [genRows, genSort.sort],
  );
  const sortedDevices = useMemo(
    () => sortRows(devices as Record<string, unknown>[], devSort.sort, cmp) as unknown as Device[],
    [devices, devSort.sort],
  );
  const sortedClients = useMemo(
    () => sortRows(clients as Record<string, unknown>[], cliSort.sort, cmp) as unknown as Client[],
    [clients, cliSort.sort],
  );
  const sortedSsids = useMemo(
    () => sortRows(ssids as Record<string, unknown>[], ssidSort.sort, cmp) as unknown as Ssid[],
    [ssids, ssidSort.sort],
  );
  const sortedVlans = useMemo(
    () => sortRows(vlans as Record<string, unknown>[], vlanSort.sort, cmp) as unknown as Vlan[],
    [vlans, vlanSort.sort],
  );
  const sortedLicenses = useMemo(
    () => sortRows(licenses as Record<string, unknown>[], licSort.sort, cmp) as unknown as License[],
    [licenses, licSort.sort],
  );
  const sortedVpnUsers = useMemo(
    () =>
      sortRows(vpnUsers as Record<string, unknown>[], vpnUserSort.sort, cmp) as unknown as VpnUser[],
    [vpnUsers, vpnUserSort.sort],
  );
  // Firewall rules carry their MX evaluation position as ``rule`` so the "#"
  // column keeps telling the truth after a sort by policy/source/etc.
  const sortedFw = useMemo(() => {
    const numbered = fw.map((r, i) => ({ ...r, rule: i + 1 }));
    return sortRows(numbered as Record<string, unknown>[], fwSort.sort, (r, k) => {
      if (k === "source") return String(r.srcCidr ?? "").toLowerCase();
      if (k === "destination") return String(r.destCidr ?? "").toLowerCase();
      return cmp(r, k);
    }) as unknown as (FwRule & { rule: number })[];
  }, [fw, fwSort.sort]);
  const sortedPf = useMemo(
    () =>
      sortRows(pf as Record<string, unknown>[], pfSort.sort, (r, k) =>
        k === "allowed" ? ((r.allowedIps as string[]) || []).join(", ").toLowerCase() : cmp(r, k),
      ) as unknown as PortForward[],
    [pf, pfSort.sort],
  );
  const sortedNat11 = useMemo(
    () =>
      sortRows(nat.oneToOne as unknown as Record<string, unknown>[], nat11Sort.sort, (r, k) =>
        k === "allowedInbound" ? ((r.allowedInbound as unknown[]) || []).length : cmp(r, k),
      ) as unknown as typeof nat.oneToOne,
    [nat, nat11Sort.sort],
  );
  const sortedNat1m = useMemo(
    () =>
      sortRows(nat.oneToMany as unknown as Record<string, unknown>[], nat1mSort.sort, (r, k) =>
        k === "portRules" ? ((r.portRules as unknown[]) || []).length : cmp(r, k),
      ) as unknown as typeof nat.oneToMany,
    [nat, nat1mSort.sort],
  );
  const sortedRoutes = useMemo(
    () =>
      sortRows(routes as Record<string, unknown>[], routeSort.sort, cmp) as unknown as StaticRoute[],
    [routes, routeSort.sort],
  );
  // "unlimited" bandwidth is null on the wire — sort it as the largest value so
  // unlimited policies group at the top/bottom instead of scattering with 0.
  const sortedGroupPolicies = useMemo(
    () =>
      sortRows(groupPolicies as Record<string, unknown>[], gpSort.sort, (r, k) => {
        if (k === "limitDown" || k === "limitUp")
          return r[k] == null ? Number.MAX_SAFE_INTEGER : Number(r[k]);
        return cmp(r, k);
      }) as unknown as GroupPolicy[],
    [groupPolicies, gpSort.sort],
  );

  // Load the customer's cloud-controller integrations — ANY vendor (Meraki,
  // UniFi, FortiGate, …). Vendor-agnostic: the UI adapts to each one's caps.
  useEffect(() => {
    setErr("");
    setIntegrations([]);
    setNiId(null);
    setNetworks([]);
    setNetId("");
    api
      .get("/api/v1/cloud-controllers")
      .then((r) => {
        const list: Integration[] = r.data || [];
        setIntegrations(list);
        if (list.length) setNiId(list[0].id);
      })
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load cloud controllers"));
  }, [currentCustomerId]);

  // Supported vendors (for the empty-state prompt — generic, not Meraki-only).
  useEffect(() => {
    api
      .get("/api/v1/cloud-controllers/vendors")
      .then((r) => setVendorList(r.data || []))
      .catch(() => setVendorList([]));
  }, []);

  // Non-Meraki controllers: capability-driven read tabs via the agnostic
  // endpoints. genTab → resource path; only tabs the vendor declares are shown.
  // Which declared action edits the tab currently on screen. Matched on the
  // capability name the tab already maps to, so no vendor knowledge here.
  const actionFor = (tabName: string, kind: "update" | "create") => {
    const cap = GEN_TAB_CAP_LOOKUP[tabName];
    // Enabled by the admin AND possible with the stored credential. The
    // second is not a formality: a UniFi API key can read every tab here and
    // cannot apply a single change.
    if (!cap || !ccActions?.writes_enabled) return null;
    if (ccActions.writes_possible === false) return null;
    return (
      (ccActions.actions || []).find(
        (a) => a.target === cap && (a.kind || "update") === kind,
      ) || null
    );
  };

  const GEN_TAB_CAP = GEN_TAB_CAP_LOOKUP;
  const GEN_CAP_PATH = GEN_CAP_PATH_LOOKUP;
  useEffect(() => {
    if (niId == null || isMeraki || !currentIntegration) {
      setGenRows([]);
      return;
    }
    const caps = currentIntegration.capabilities || [];
    const cap = GEN_TAB_CAP[genTab];
    if (!cap || !caps.includes(cap)) {
      setGenRows([]);
      return;
    }
    setGenLoading(true);
    api
      // The path IS the capability unless the lookup renames it. Without the
      // fallback a newly declared capability fetches /undefined and the tab
      // shows "nothing returned" - a lie that looks like data.
      .get(`/api/v1/cloud-controllers/${niId}/${GEN_CAP_PATH[cap] || cap}`)
      .then((r) => setGenRows(r.data || []))
      .catch(() => setGenRows([]))
      .finally(() => setGenLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [niId, isMeraki, genTab, currentIntegration, vNonce]);

  // When a MERAKI integration changes, load networks + org-wide devices + licenses.
  useEffect(() => {
    if (niId == null || !isMeraki) return;
    setErr("");
    setNetworks([]);
    setNetId("");
    setDevices([]);
    setLicenses([]);
    setLicenseOverview(null);
    setLoading(true);
    Promise.all([
      api.get(`/api/v1/meraki/${niId}/networks`).then((r) => r.data as Network[]),
      api.get(`/api/v1/meraki/${niId}/devices`).then((r) => r.data as Device[]),
      api
        .get(`/api/v1/meraki/${niId}/licenses`)
        .then((r) => r.data as License[])
        .catch(() => []),
      api
        .get(`/api/v1/meraki/${niId}/license-overview`)
        .then((r) => r.data as LicenseOverview)
        .catch(() => null),
    ])
      .then(([nets, devs, lics, ovr]) => {
        setNetworks(nets || []);
        setDevices(devs || []);
        setLicenses(lics || []);
        setLicenseOverview(ovr || null);
        if (nets && nets.length) setNetId(nets[0].id);
      })
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load Meraki data"))
      .finally(() => setLoading(false));
  }, [niId, isMeraki, reloadKey]);

  // Per-network reads when the active network or tab needs them.
  useEffect(() => {
    if (niId == null || !netId || !isMeraki) return;
    const base = `/api/v1/meraki/${niId}/networks/${netId}`;
    const load = (path: string, set: (v: any) => void) => {
      setLoading(true);
      setErr("");
      api
        .get(`${base}${path}`)
        .then((r) => set(r.data || []))
        .catch((e) => setErr(e.response?.data?.detail || "Failed to load from Meraki"))
        .finally(() => setLoading(false));
    };
    if (tab === "Clients") {
      load("/clients", setClients);
      api
        .get(`${base}/client-policies`)
        .then((r) => {
          const m: Record<string, string> = {};
          for (const cp of r.data || []) {
            if (cp.clientId) m[cp.clientId] = cp.policy || "Normal";
            if (cp.mac) m[cp.mac] = cp.policy || "Normal";
          }
          setClientPolicies(m);
        })
        .catch(() => setClientPolicies({}));
    } else if (tab === "VPN") {
      load("/site-to-site-vpn", (d: SiteToSite) => setS2s(d || {}));
      api
        .get(`/api/v1/meraki/${niId}/third-party-vpn-peers`)
        .then((r) => setIpsec(r.data || []))
        .catch(() => setIpsec([]));
      api
        .get(`${base}/client-vpn-info`)
        .then((r) => setClientVpn(r.data || null))
        .catch(() => setClientVpn(null));
    } else if (tab === "Group Policies") load("/group-policies", setGroupPolicies);
    else if (tab === "VPN Users") load("/vpn-users", setVpnUsers);
    else if (tab === "Wireless") load("/ssids", setSsids);
    // (vNonce in deps → this refetch re-runs after a successful action to validate it)
    else if (tab === "VLANs") {
      load("/vlans", setVlans);
      api
        .get(`${base}/appliance-ports`)
        .then((r) => setApplPorts(r.data || []))
        .catch(() => setApplPorts([]));
    } else if (tab === "Firewall") {
      load("/firewall/l3", setFw);
      api
        .get(`${base}/firewall/port-forwarding`)
        .then((r) => setPf(r.data || []))
        .catch(() => setPf([]));
      api
        .get(`${base}/firewall/nat`)
        .then((r) => setNat(r.data || { oneToOne: [], oneToMany: [] }))
        .catch(() => setNat({ oneToOne: [], oneToMany: [] }));
    } else if (tab === "Routing") load("/static-routes", setRoutes);
  }, [niId, netId, tab, reloadKey, vNonce, isMeraki]);

  // Publish the current context so the ✨ AI Assist can act on Meraki with
  // real identifiers (network_integration_id + network_id + the visible rows).
  const screenItems = useMemo<Record<string, unknown>[]>(() => {
    const ctx: Record<string, unknown> = {
      _context: "meraki",
      network_integration_id: niId,
      network_id: netId || null,
      network_name: netName || null,
    };
    let rows: Record<string, unknown>[] = [];
    if (tab === "Devices")
      rows = devices.map((d) => ({ serial: d.serial, name: d.hostname, model: d.model, status: d.status }));
    else if (tab === "Wireless")
      rows = ssids.map((s) => ({ number: s.number, name: s.name, enabled: s.enabled }));
    else if (tab === "VLANs")
      rows = vlans.map((v) => ({ vlan_id: v.id, name: v.name, subnet: v.subnet }));
    else if (tab === "Clients")
      rows = clients.map((c) => ({ description: c.description, ip: c.ip, mac: c.mac, vlan: c.vlan }));
    return [ctx, ...rows];
  }, [niId, netId, netName, tab, devices, ssids, vlans, clients]);
  usePublishScreenData(
    // The controller's own name, not a vendor's: this page serves firewalls
    // and wireless controllers alike, and the label is what the assistant
    // tells the operator it is looking at.
    `Cloud controller${currentIntegration?.name ? ` — ${currentIntegration.name}` : ""}${
      netName ? ` — ${netName}` : ""
    }`,
    screenItems,
    netId ? `network ${netId}` : undefined,
  );

  const th = "text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300";
  const td = "px-3 py-2 text-gray-800 dark:text-gray-100";
  const rowCls = "border-t border-gray-100 dark:border-gray-700/60";

  const needsNetwork =
    tab === "Clients" ||
    tab === "VPN" ||
    tab === "Group Policies" ||
    tab === "VPN Users" ||
    tab === "Wireless" ||
    tab === "VLANs" ||
    tab === "Firewall" ||
    tab === "Routing";
  const proposeRoute = () => {
    if (!editRoute) return;
    const f: Record<string, unknown> = {
      network_id: netId,
      name: editRoute.name,
      subnet: editRoute.subnet,
      gatewayIp: editRoute.gatewayIp,
      enabled: editRoute.enabled,
    };
    if (editRoute.id) f.static_route_id = editRoute.id;
    propose("meraki_set_static_route", f);
    setEditRoute(null);
  };

  // ── NAT editing (Firewall tab) ───────────────────────────────────────────
  const start1to1Edit = () =>
    setNat1to1Edit(
      nat.oneToOne.map((r) => {
        const ai = (r.allowedInbound && r.allowedInbound[0]) || {};
        return {
          name: r.name || "",
          publicIp: r.publicIp || "",
          lanIp: r.lanIp || "",
          uplink: r.uplink || "internet1",
          protocol: ai.protocol || "any",
          ports: (ai.destinationPorts || ["any"]).join(","),
          allowedIps: (ai.allowedIps || ["any"]).join(","),
        };
      }),
    );
  const upd1to1 = (i: number, patch: Partial<Nat1to1Row>) =>
    setNat1to1Edit((p) => (p || []).map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const propose1to1 = () => {
    const rules = (nat1to1Edit || []).map((r) => ({
      name: r.name,
      publicIp: r.publicIp,
      lanIp: r.lanIp,
      uplink: r.uplink,
      allowedInbound: r.protocol
        ? [
            {
              protocol: r.protocol,
              destinationPorts: r.ports ? r.ports.split(",").map((s) => s.trim()) : ["any"],
              allowedIps: r.allowedIps ? r.allowedIps.split(",").map((s) => s.trim()) : ["any"],
            },
          ]
        : [],
    }));
    propose("meraki_set_one_to_one_nat", { network_id: netId, rules });
    setNat1to1Edit(null);
  };
  const start1manyEdit = () => {
    const rows: Nat1manyRow[] = [];
    for (const r of nat.oneToMany)
      for (const pr of r.portRules || [])
        rows.push({
          publicIp: r.publicIp || "",
          uplink: r.uplink || "internet1",
          name: pr.name || "",
          protocol: pr.protocol || "tcp",
          publicPort: pr.publicPort || "",
          localIp: pr.localIp || "",
          localPort: pr.localPort || "",
          allowedIps: (pr.allowedIps || ["any"]).join(","),
        });
    setNat1manyEdit(rows);
  };
  const upd1many = (i: number, patch: Partial<Nat1manyRow>) =>
    setNat1manyEdit((p) => (p || []).map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const propose1many = () => {
    const groups: Record<
      string,
      { publicIp: string; uplink: string; portRules: Record<string, unknown>[] }
    > = {};
    for (const r of nat1manyEdit || []) {
      const k = `${r.publicIp}|${r.uplink}`;
      (groups[k] = groups[k] || { publicIp: r.publicIp, uplink: r.uplink, portRules: [] }).portRules.push({
        name: r.name,
        protocol: r.protocol,
        publicPort: r.publicPort,
        localIp: r.localIp,
        localPort: r.localPort,
        allowedIps: r.allowedIps ? r.allowedIps.split(",").map((s) => s.trim()) : ["any"],
      });
    }
    propose("meraki_set_one_to_many_nat", { network_id: netId, rules: Object.values(groups) });
    setNat1manyEdit(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Cloud Controllers{current?.name ? ` — ${current.name}` : ""}
        </h1>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Manage this customer's network cloud controllers. Each connected vendor shows the
        capabilities its API supports. To make a change, type it into the{" "}
        <strong>✨ AI Assist</strong> card at the top of the page — changes are proposed for your
        approval.
      </p>

      {currentCustomerId == null && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-200 rounded-lg px-4 py-3 text-sm">
          Select a specific customer in the top selector to view its cloud controllers.
        </div>
      )}

      {currentCustomerId != null && integrations.length === 0 && !err && (
        <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl px-4 py-6 text-sm text-gray-600 dark:text-gray-300">
          <p>
            No cloud controller connected for this customer yet. Connect one under{" "}
            <Link to="/network-discovery" className="text-blue-600 dark:text-blue-400 underline">
              Discovery &amp; integrations
            </Link>
            .
          </p>
          {vendorList.length > 0 && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Supported: {vendorList.map((v) => v.label).join(", ")}.
            </p>
          )}
        </div>
      )}

      {err && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/60 text-red-800 dark:text-red-200 rounded-lg px-4 py-3 text-sm mb-4">
          {err}
        </div>
      )}

      {integrations.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <label className="text-sm text-gray-600 dark:text-gray-300">
            Controller:{" "}
            <select
              value={niId ?? ""}
              onChange={(e) => setNiId(Number(e.target.value))}
              className="ml-1 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-sm"
            >
              {integrations.map((i) => (
                <option key={i.id} value={i.id}>
                  {(i.vendor_label || i.vendor) + " · " + i.name}
                </option>
              ))}
            </select>
          </label>
          {currentIntegration && (
            <span className="text-xs flex items-center gap-1.5">
              <span
                className={`px-2 py-0.5 rounded-full ${writesOn ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"}`}
                title={
                  isMeraki
                    ? "Meraki is verified live; writes enabled."
                    : "This vendor's write actions are best-effort/unverified — validate on your controller before enabling."
                }
              >
                Writes {writesOn ? "enabled" : "disabled"}
              </span>
              {canManageRole && (
                <ActionButton onAction={() => toggleWrites()} variant="raw" className="px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40" pendingLabel="Working…">
                  {writesOn ? "Disable" : "Enable"}
                </ActionButton>
              )}
            </span>
          )}
          {networks.length > 0 && (
            <label className="text-sm text-gray-600 dark:text-gray-300">
              Network:{" "}
              <select
                value={netId}
                onChange={(e) => setNetId(e.target.value)}
                className="ml-1 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-sm"
              >
                {networks.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {isMeraki && (
            <div className="ml-auto flex items-center gap-2">
              {syncMsg && <span className="text-xs text-gray-400">{syncMsg}</span>}
              {canManage && (
                <button
                  onClick={() =>
                    setNewNet({ name: "", productTypes: ["appliance"], timezone: "" })
                  }
                  disabled={niId == null}
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
                >
                  + New network
                </button>
              )}
              <button
                onClick={syncNow}
                disabled={syncing || niId == null}
                className="text-sm px-3 py-1.5 rounded-lg border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/40 disabled:opacity-50"
                title="Re-sync devices + licenses from Meraki now"
              >
                {syncing ? "Syncing…" : "↻ Sync now"}
              </button>
            </div>
          )}
        </div>
      )}

      {newNet && (
        <div className="mb-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3">
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
            New Meraki network
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-gray-500 dark:text-gray-400">
              Name
              <input
                value={newNet.name}
                onChange={(e) => setNewNet({ ...newNet, name: e.target.value })}
                className="block mt-0.5 w-56 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
              />
            </label>
            <label className="text-xs text-gray-500 dark:text-gray-400">
              Time zone (optional)
              <input
                value={newNet.timezone}
                onChange={(e) => setNewNet({ ...newNet, timezone: e.target.value })}
                placeholder="America/New_York"
                className="block mt-0.5 w-48 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
              />
            </label>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Product types
              <div className="mt-0.5 flex flex-wrap gap-2">
                {NET_PRODUCT_TYPES.map((pt) => (
                  <label key={pt} className="flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={newNet.productTypes.includes(pt)}
                      onChange={(e) =>
                        setNewNet({
                          ...newNet,
                          productTypes: e.target.checked
                            ? [...newNet.productTypes, pt]
                            : newNet.productTypes.filter((x) => x !== pt),
                        })
                      }
                    />
                    {pt}
                  </label>
                ))}
              </div>
            </div>
            <button
              onClick={createNetwork}
              disabled={!newNet.name.trim() || !newNet.productTypes.length}
              className="bg-indigo-600 text-white text-sm px-3 py-1.5 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              Create network
            </button>
            <button
              onClick={() => setNewNet(null)}
              className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {proposeErr && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/60 text-red-800 dark:text-red-200 rounded-lg px-4 py-2 text-sm mb-3">
          {proposeErr}
        </div>
      )}

      {pending.length > 0 && (
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Pending approval — nothing runs until you approve
          </h2>
          <div className="space-y-2">
            {pending.map((a) => (
              <ProposedActionCard key={a.id} action={a} onChange={(u) => patchPending(a.id, u)} />
            ))}
          </div>
        </div>
      )}

      {integrations.length > 0 && isMeraki && (
        <>
          <div className="flex flex-wrap gap-1 mb-3 border-b border-gray-200 dark:border-gray-700">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-2 text-sm -mb-px border-b-2 ${
                  tab === t
                    ? "border-indigo-600 text-indigo-700 dark:text-indigo-300 font-medium"
                    : "border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
            {loading && (
              <p className="text-sm text-gray-400 text-center py-6">Loading from Meraki…</p>
            )}
            {!loading && needsNetwork && !netId && (
              <p className="text-sm text-gray-400 text-center py-6">
                This network has no data for {tab.toLowerCase()}.
              </p>
            )}

            {!loading && tab === "Devices" && (
              <>
                {editDevice && (
                  <div
                    ref={editorRef}
                    className="p-3 bg-indigo-50/60 dark:bg-indigo-900/10 border-b border-gray-100 dark:border-gray-700/60 flex flex-wrap items-end gap-2"
                  >
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Edit {editDevice.serial}
                    </div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Name
                      <input
                        value={editDevice.name}
                        onChange={(e) => setEditDevice({ ...editDevice, name: e.target.value })}
                        className="block mt-0.5 w-40 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      />
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Address
                      <input
                        value={editDevice.address}
                        onChange={(e) => setEditDevice({ ...editDevice, address: e.target.value })}
                        className="block mt-0.5 w-48 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      />
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Tags (space/comma)
                      <input
                        value={editDevice.tags}
                        onChange={(e) => setEditDevice({ ...editDevice, tags: e.target.value })}
                        className="block mt-0.5 w-40 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      />
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Notes
                      <input
                        value={editDevice.notes}
                        onChange={(e) => setEditDevice({ ...editDevice, notes: e.target.value })}
                        className="block mt-0.5 w-48 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      />
                    </label>
                    <button
                      onClick={proposeDevice}
                      className="bg-indigo-600 text-white text-sm px-3 py-1.5 rounded-lg hover:bg-indigo-700"
                    >
                      Propose
                    </button>
                    <button
                      onClick={() => setEditDevice(null)}
                      className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                    <SortableTh label="Name" sortKey="hostname" sort={devSort.sort} onSort={devSort.toggle} />
                    <SortableTh label="Model" sortKey="model" sort={devSort.sort} onSort={devSort.toggle} hide="hidden md:table-cell" />
                    <SortableTh label="Serial" sortKey="serial" sort={devSort.sort} onSort={devSort.toggle} hide="hidden xl:table-cell" />
                    <SortableTh label="Status" sortKey="status" sort={devSort.sort} onSort={devSort.toggle} />
                    <SortableTh label="IP" sortKey="ip_address" sort={devSort.sort} onSort={devSort.toggle} hide="hidden sm:table-cell" />
                    <SortableTh label="Firmware" sortKey="firmware" sort={devSort.sort} onSort={devSort.toggle} hide="hidden lg:table-cell" />
                    {canManage && <th className={th}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedDevices.map((d) => (
                    <tr
                      key={d.serial}
                      className={rowCls}
                      title={`${d.hostname || "(unnamed)"} — ${d.model || ""} ${d.serial || ""}, ${d.status || "unknown"}, ${d.ip_address || ""}, fw ${d.firmware || "?"}`}
                    >
                      <td className={td}>{d.hostname || "—"}</td>
                      <td className={`${td} hidden md:table-cell`}>{d.model}</td>
                      <td className={`${td} font-mono text-xs hidden xl:table-cell`}>{d.serial}</td>
                      <td className={td}>{statusPill(d.status)}</td>
                      <td className={`${td} hidden sm:table-cell`}>{d.ip_address}</td>
                      <td className={`${td} hidden lg:table-cell`}>{d.firmware}</td>
                      {canManage && (
                        <td className={td}>
                          <div className="flex gap-1">
                            <button
                              onClick={() => propose("meraki_reboot_device", { serial: d.serial })}
                              className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                            >
                              Reboot
                            </button>
                            <button
                              onClick={() => propose("meraki_blink_leds", { serial: d.serial })}
                              className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                            >
                              Blink
                            </button>
                            <button
                              onClick={() =>
                                setEditDevice({
                                  serial: d.serial || "",
                                  name: d.hostname || "",
                                  address: "",
                                  notes: "",
                                  tags: "",
                                })
                              }
                              className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                            >
                              Edit
                            </button>
                            {aiOn && (
                              <HoverSuggest
                                niId={niId}
                                onProposed={handleProposed}
                                kind="device"
                                item={{
                                  serial: d.serial,
                                  name: d.hostname,
                                  model: d.model,
                                  status: d.status,
                                  ip: d.ip_address,
                                  firmware: d.firmware,
                                }}
                              />
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {devices.length === 0 && (
                    <tr>
                      <td className="px-3 py-6 text-center text-gray-400" colSpan={canManage ? 7 : 6}>
                        No devices in this org.
                      </td>
                    </tr>
                  )}
                </tbody>
                </table>
              </>
            )}

            {!loading && tab === "Clients" && netId && (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <SortableTh label="Client" sortKey="description" sort={cliSort.sort} onSort={cliSort.toggle} />
                    <SortableTh label="IP" sortKey="ip" sort={cliSort.sort} onSort={cliSort.toggle} />
                    <SortableTh label="MAC" sortKey="mac" sort={cliSort.sort} onSort={cliSort.toggle} />
                    <SortableTh label="VLAN" sortKey="vlan" sort={cliSort.sort} onSort={cliSort.toggle} />
                    <SortableTh label="SSID" sortKey="ssid" sort={cliSort.sort} onSort={cliSort.toggle} />
                    <SortableTh label="Manufacturer" sortKey="manufacturer" sort={cliSort.sort} onSort={cliSort.toggle} />
                    <SortableTh label="Status" sortKey="status" sort={cliSort.sort} onSort={cliSort.toggle} />
                    <th className={th}>Policy</th>
                    {canManage && <th className={th}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedClients.map((c) => (
                    <tr
                      key={c.id || c.mac}
                      className={rowCls}
                      title={`${c.description || "(unknown)"} — ${c.ip || ""} / ${c.mac || ""}, VLAN ${c.vlan ?? "?"}, ${c.ssid || "wired"}, ${c.manufacturer || ""}`}
                    >
                      <td className={td}>{c.description || "—"}</td>
                      <td className={td}>{c.ip}</td>
                      <td className={`${td} font-mono text-xs`}>{c.mac}</td>
                      <td className={td}>{c.vlan ?? ""}</td>
                      <td className={td}>{c.ssid || ""}</td>
                      <td className={td}>{c.manufacturer || ""}</td>
                      <td className={td}>{statusPill(c.status)}</td>
                      <td className={td}>
                        {(() => {
                          const p = clientPol(c);
                          const blocked = p === "Blocked";
                          return (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full ${blocked ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" : "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"}`}
                            >
                              {blocked ? "Blocked" : p === "Normal" ? "Allowed" : p}
                            </span>
                          );
                        })()}
                      </td>
                      {canManage && (
                        <td className={td}>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setClientPolicy(c, "Blocked")}
                              disabled={clientPol(c) === "Blocked"}
                              className="text-xs px-2 py-0.5 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-40"
                            >
                              Block
                            </button>
                            <button
                              onClick={() => setClientPolicy(c, "Normal")}
                              disabled={clientPol(c) !== "Blocked"}
                              className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-40"
                            >
                              Allow
                            </button>
                            {aiOn && (
                              <HoverSuggest
                                niId={niId}
                                onProposed={handleProposed}
                                kind="client"
                                networkId={netId}
                                item={{
                                  client_id: c.id || c.mac,
                                  description: c.description,
                                  ip: c.ip,
                                  mac: c.mac,
                                  manufacturer: c.manufacturer,
                                }}
                              />
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {clients.length === 0 && (
                    <tr>
                      <td className="px-3 py-6 text-center text-gray-400" colSpan={canManage ? 9 : 8}>
                        No clients seen in the last 24h.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {!loading && tab === "VPN" && netId && (
              <>
                <div className="p-3 border-b border-gray-100 dark:border-gray-700/60">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Client VPN (end-user IPSec / L2TP)
                  </h3>
                  <div className="mt-1 text-xs text-gray-600 dark:text-gray-300 space-y-0.5">
                    <div>
                      Server address users connect to:{" "}
                      <span className="font-mono">{clientVpn?.dynamicDnsHostname || "—"}</span>
                      {clientVpn?.dynamicDnsEnabled === false &&
                        " (dynamic DNS off — use the MX WAN IP)"}
                    </div>
                    <div>Deployment mode: {clientVpn?.deploymentMode || "—"}</div>
                    <div>
                      User accounts:{" "}
                      <button
                        onClick={() => setTab("VPN Users")}
                        className="text-indigo-600 dark:text-indigo-300 underline"
                      >
                        VPN Users
                      </button>{" "}
                      tab (add / enable / disable / reset password).
                    </div>
                  </div>
                  <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">
                    Meraki's API doesn't expose the Client VPN shared secret (PSK), client subnet,
                    DNS, or auth type — set/read those in Dashboard → Security &amp; SD-WAN → Client
                    VPN.
                  </p>
                </div>
                <div className="p-2 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Site-to-site VPN (AutoVPN)
                    </h3>
                    <p className="text-[11px] text-gray-400">
                      Mode: <span className="font-medium">{s2s?.mode || "—"}</span>
                    </p>
                  </div>
                  {canManage &&
                    (s2sEdit === null ? (
                      <button
                        onClick={startS2sEdit}
                        className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                      >
                        Edit
                      </button>
                    ) : (
                      <span className="flex gap-1">
                        <button
                          onClick={proposeS2s}
                          className="text-xs px-3 py-1 bg-indigo-600 text-white rounded"
                        >
                          Propose
                        </button>
                        <button
                          onClick={() => setS2sEdit(null)}
                          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded"
                        >
                          Cancel
                        </button>
                      </span>
                    ))}
                </div>
                {s2sEdit === null ? (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <th className={th}>Local subnet</th>
                        <th className={th}>In VPN</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(s2s?.subnets || []).map((sn, i) => (
                        <tr key={i} className={rowCls}>
                          <td className={`${td} font-mono text-xs`}>{sn.localSubnet}</td>
                          <td className={td}>
                            {sn.useVpn ? (
                              <span className="text-green-700 dark:text-green-400">yes</span>
                            ) : (
                              "no"
                            )}
                          </td>
                        </tr>
                      ))}
                      {(!s2s?.subnets || s2s.subnets.length === 0) && (
                        <tr>
                          <td className="px-3 py-3 text-center text-gray-400" colSpan={2}>
                            No local subnets / AutoVPN not configured.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-3 bg-indigo-50/60 dark:bg-indigo-900/10 border-y border-gray-100 dark:border-gray-700/60">
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Mode
                      <select
                        value={s2sEdit.mode}
                        onChange={(e) => setS2sEdit({ ...s2sEdit, mode: e.target.value })}
                        className="ml-2 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      >
                        <option value="none">none</option>
                        <option value="spoke">spoke</option>
                        <option value="hub">hub</option>
                      </select>
                    </label>
                    {s2sEdit.mode === "spoke" && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                            Hubs (this spoke's tunnels)
                          </span>
                          <button
                            onClick={() =>
                              setS2sEdit({
                                ...s2sEdit,
                                hubs: [...s2sEdit.hubs, { hubId: "", useDefaultRoute: false }],
                              })
                            }
                            className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded"
                          >
                            + Add hub
                          </button>
                        </div>
                        {s2sEdit.hubs.map((h, i) => (
                          <div key={i} className="mt-1 flex flex-wrap items-center gap-2">
                            <input
                              value={h.hubId}
                              placeholder="Hub network ID (N_… / L_…)"
                              onChange={(e) =>
                                setS2sEdit({
                                  ...s2sEdit,
                                  hubs: s2sEdit.hubs.map((x, idx) =>
                                    idx === i ? { ...x, hubId: e.target.value } : x,
                                  ),
                                })
                              }
                              className="w-64 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-xs font-mono"
                            />
                            <label className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={h.useDefaultRoute}
                                onChange={(e) =>
                                  setS2sEdit({
                                    ...s2sEdit,
                                    hubs: s2sEdit.hubs.map((x, idx) =>
                                      idx === i ? { ...x, useDefaultRoute: e.target.checked } : x,
                                    ),
                                  })
                                }
                              />
                              default route
                            </label>
                            <button
                              onClick={() =>
                                setS2sEdit({
                                  ...s2sEdit,
                                  hubs: s2sEdit.hubs.filter((_, idx) => idx !== i),
                                })
                              }
                              className="text-xs px-1 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        {s2sEdit.hubs.length === 0 && (
                          <p className="text-[11px] text-gray-400 mt-1">
                            Add the hub network ID(s) this spoke should tunnel to.
                          </p>
                        )}
                      </div>
                    )}
                    <table className="w-full text-sm mt-2">
                      <thead className="bg-gray-50 dark:bg-gray-900/50">
                        <tr>
                          <th className={th}>Local subnet</th>
                          <th className={th}>In VPN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s2sEdit.subnets.map((sn, i) => (
                          <tr key={i} className={rowCls}>
                            <td className={`${td} font-mono text-xs`}>{sn.localSubnet}</td>
                            <td className={td}>
                              <input
                                type="checkbox"
                                checked={sn.useVpn}
                                onChange={(e) =>
                                  setS2sEdit({
                                    ...s2sEdit,
                                    subnets: s2sEdit.subnets.map((x, idx) =>
                                      idx === i ? { ...x, useVpn: e.target.checked } : x,
                                    ),
                                  })
                                }
                              />
                            </td>
                          </tr>
                        ))}
                        {s2sEdit.subnets.length === 0 && (
                          <tr>
                            <td className="px-3 py-3 text-center text-gray-400" colSpan={2}>
                              No local subnets to include.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="p-2 pt-3 flex items-center justify-between border-t border-gray-100 dark:border-gray-700/60">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      IPSec VPN — site-to-site (non-Meraki peers)
                    </h3>
                    <p className="text-[11px] text-gray-400">
                      Tunnels to non-Meraki firewalls/clouds. Add a peer = add a site-to-site tunnel.
                    </p>
                  </div>
                  {canManage &&
                    (ipsecEdit === null ? (
                      <span className="flex gap-1">
                        <button
                          onClick={addIpsecTunnel}
                          className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                        >
                          + Add IPSec tunnel
                        </button>
                        <button
                          onClick={startIpsecEdit}
                          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                        >
                          Edit
                        </button>
                      </span>
                    ) : (
                      <span className="flex gap-1">
                        <button
                          onClick={addIpsecTunnel}
                          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded"
                        >
                          + Add peer
                        </button>
                        <button
                          onClick={proposeIpsec}
                          className="text-xs px-3 py-1 bg-indigo-600 text-white rounded"
                        >
                          Propose
                        </button>
                        <button
                          onClick={() => setIpsecEdit(null)}
                          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded"
                        >
                          Cancel
                        </button>
                      </span>
                    ))}
                </div>
                {ipsecEdit === null ? (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <th className={th}>Name</th>
                        <th className={th}>Public IP</th>
                        <th className={th}>Private subnets</th>
                        <th className={th}>IKE</th>
                        <th className={th}>Preset</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ipsec.map((p, i) => (
                        <tr key={i} className={rowCls}>
                          <td className={td}>{p.name}</td>
                          <td className={`${td} font-mono text-xs`}>{p.publicIp}</td>
                          <td className={`${td} font-mono text-xs`}>
                            {(p.privateSubnets || []).join(", ")}
                          </td>
                          <td className={td}>IKEv{p.ikeVersion}</td>
                          <td className={td}>
                            {p.ipsecPolicies &&
                            (!p.ipsecPoliciesPreset || p.ipsecPoliciesPreset === "custom") ? (
                              <span className="text-indigo-700 dark:text-indigo-300">
                                custom (P1/P2)
                              </span>
                            ) : (
                              p.ipsecPoliciesPreset || "default"
                            )}
                          </td>
                        </tr>
                      ))}
                      {ipsec.length === 0 && (
                        <tr>
                          <td className="px-3 py-3 text-center text-gray-400" colSpan={5}>
                            No third-party IPSec peers.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-2 space-y-2">
                    {ipsecEdit.map((r, i) => {
                      const fld = "border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs";
                      const lbl = "text-xs text-gray-500 dark:text-gray-400";
                      return (
                        <div
                          key={i}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-2"
                        >
                          <div className="flex flex-wrap items-end gap-2">
                            <label className={lbl}>
                              Name
                              <input value={r.name} onChange={(e) => updIpsec(i, { name: e.target.value })} className={`block mt-0.5 w-28 ${fld}`} />
                            </label>
                            <label className={lbl}>
                              Public IP
                              <input value={r.publicIp} onChange={(e) => updIpsec(i, { publicIp: e.target.value })} className={`block mt-0.5 w-32 font-mono ${fld}`} />
                            </label>
                            <label className={lbl}>
                              Remote subnets
                              <input value={r.privateSubnets} placeholder="10.0.0.0/24,…" onChange={(e) => updIpsec(i, { privateSubnets: e.target.value })} className={`block mt-0.5 w-44 font-mono ${fld}`} />
                            </label>
                            <label className={lbl}>
                              Pre-shared secret
                              <input value={r.secret} onChange={(e) => updIpsec(i, { secret: e.target.value })} className={`block mt-0.5 w-40 ${fld}`} />
                            </label>
                            <label className={lbl}>
                              IKE
                              <select value={r.ikeVersion} onChange={(e) => updIpsec(i, { ikeVersion: e.target.value })} className={`block mt-0.5 ${fld}`}>
                                <option value="1">1</option>
                                <option value="2">2</option>
                              </select>
                            </label>
                            <button
                              onClick={() => setIpsecEdit((p) => (p || []).filter((_, idx) => idx !== i))}
                              className="ml-auto text-xs px-2 py-0.5 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded"
                            >
                              ✕ Remove tunnel
                            </button>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-3">
                            <label className="text-xs flex items-center gap-1">
                              <input type="radio" checked={r.policyMode === "preset"} onChange={() => updIpsec(i, { policyMode: "preset" })} />
                              Preset
                            </label>
                            {r.policyMode === "preset" && (
                              <select value={r.preset} onChange={(e) => updIpsec(i, { preset: e.target.value })} className={fld}>
                                <option value="default">default</option>
                                <option value="aws">aws</option>
                                <option value="azure">azure</option>
                                <option value="gcp">gcp</option>
                                <option value="zscaler">zscaler</option>
                                <option value="umbrella">umbrella</option>
                              </select>
                            )}
                            <label className="text-xs flex items-center gap-1">
                              <input type="radio" checked={r.policyMode === "custom"} onChange={() => updIpsec(i, { policyMode: "custom" })} />
                              Custom Phase 1 / Phase 2
                            </label>
                          </div>
                          {r.policyMode === "custom" && (
                            <div className="mt-2 grid md:grid-cols-2 gap-3">
                              <div className="border border-gray-200 dark:border-gray-700 rounded p-2">
                                <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                                  Phase 1 (IKE)
                                </div>
                                <div className="space-y-1">
                                  <label className={`block ${lbl}`}>
                                    Encryption
                                    <input value={r.ikeCipher} placeholder="aes256,aes192,aes128,tripledes" onChange={(e) => updIpsec(i, { ikeCipher: e.target.value })} className={`block mt-0.5 w-full font-mono ${fld}`} />
                                  </label>
                                  <label className={`block ${lbl}`}>
                                    Authentication
                                    <input value={r.ikeAuth} placeholder="sha256,sha1,md5" onChange={(e) => updIpsec(i, { ikeAuth: e.target.value })} className={`block mt-0.5 w-full font-mono ${fld}`} />
                                  </label>
                                  <label className={`block ${lbl}`}>
                                    DH group
                                    <input value={r.ikeDH} placeholder="group14,group2,group5,group19" onChange={(e) => updIpsec(i, { ikeDH: e.target.value })} className={`block mt-0.5 w-full font-mono ${fld}`} />
                                  </label>
                                  <label className={`block ${lbl}`}>
                                    PRF (optional)
                                    <input value={r.ikePrf} placeholder="prfsha256,prfsha1,default" onChange={(e) => updIpsec(i, { ikePrf: e.target.value })} className={`block mt-0.5 w-full font-mono ${fld}`} />
                                  </label>
                                  <label className={`block ${lbl}`}>
                                    Lifetime (s)
                                    <input value={r.ikeLifetime} onChange={(e) => updIpsec(i, { ikeLifetime: e.target.value })} className={`block mt-0.5 w-28 font-mono ${fld}`} />
                                  </label>
                                </div>
                              </div>
                              <div className="border border-gray-200 dark:border-gray-700 rounded p-2">
                                <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                                  Phase 2 (ESP)
                                </div>
                                <div className="space-y-1">
                                  <label className={`block ${lbl}`}>
                                    Encryption
                                    <input value={r.childCipher} placeholder="aes256,aes192,aes128,tripledes" onChange={(e) => updIpsec(i, { childCipher: e.target.value })} className={`block mt-0.5 w-full font-mono ${fld}`} />
                                  </label>
                                  <label className={`block ${lbl}`}>
                                    Authentication
                                    <input value={r.childAuth} placeholder="sha256,sha1,md5" onChange={(e) => updIpsec(i, { childAuth: e.target.value })} className={`block mt-0.5 w-full font-mono ${fld}`} />
                                  </label>
                                  <label className={`block ${lbl}`}>
                                    PFS group
                                    <input value={r.childPfs} placeholder="group14 or disabled" onChange={(e) => updIpsec(i, { childPfs: e.target.value })} className={`block mt-0.5 w-full font-mono ${fld}`} />
                                  </label>
                                  <label className={`block ${lbl}`}>
                                    Lifetime (s)
                                    <input value={r.childLifetime} onChange={(e) => updIpsec(i, { childLifetime: e.target.value })} className={`block mt-0.5 w-28 font-mono ${fld}`} />
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {ipsecEdit.length === 0 && (
                      <p className="text-center text-gray-400 py-3 text-sm">
                        No peers — add one above (proposing an empty list clears all IPSec peers).
                      </p>
                    )}
                    <p className="text-[11px] text-gray-400">
                      Algorithm fields are comma-separated Meraki tokens. Phase 1 cipher:
                      aes256/aes192/aes128/tripledes; auth: sha256/sha1/md5; DH:
                      group2/5/14/15/16/19/20/21/24; PRF: prfsha256/prfsha1/default. Phase 2 PFS:
                      disabled or groupN.
                    </p>
                  </div>
                )}
                <p className="text-xs text-gray-400 px-3 py-2 border-t border-gray-100 dark:border-gray-700/60">
                  Client VPN accounts are managed in the{" "}
                  <button
                    onClick={() => setTab("VPN Users")}
                    className="text-indigo-600 dark:text-indigo-300 underline"
                  >
                    VPN Users
                  </button>{" "}
                  tab. Meraki masks IPSec peer secrets on read — re-enter the secret when editing a
                  peer.
                </p>
              </>
            )}

            {!loading && tab === "VPN Users" && netId && (
              <>
                {vpnMsg && (
                  <div className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700/60">
                    {vpnMsg}
                  </div>
                )}
                {canManage && (
                  <div className="p-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Client VPN users
                    </h3>
                    {!vpnAdd && (
                      <button
                        onClick={() =>
                          setVpnAdd({ email: "", name: "", password: "", emailToUser: false })
                        }
                        className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                      >
                        + Add user
                      </button>
                    )}
                  </div>
                )}
                {vpnAdd && (
                  <div className="p-3 bg-indigo-50/60 dark:bg-indigo-900/10 border-y border-gray-100 dark:border-gray-700/60 flex flex-wrap items-end gap-2">
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Email
                      <input
                        value={vpnAdd.email}
                        onChange={(e) => setVpnAdd({ ...vpnAdd, email: e.target.value })}
                        className="block mt-0.5 w-52 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      />
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Name
                      <input
                        value={vpnAdd.name}
                        onChange={(e) => setVpnAdd({ ...vpnAdd, name: e.target.value })}
                        className="block mt-0.5 w-40 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      />
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Password
                      <input
                        type="password"
                        value={vpnAdd.password}
                        onChange={(e) => setVpnAdd({ ...vpnAdd, password: e.target.value })}
                        className="block mt-0.5 w-40 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      />
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={vpnAdd.emailToUser}
                        onChange={(e) => setVpnAdd({ ...vpnAdd, emailToUser: e.target.checked })}
                      />{" "}
                      email to user
                    </label>
                    <button
                      onClick={createVpnUser}
                      disabled={!vpnAdd.email || !vpnAdd.password}
                      className="bg-indigo-600 text-white text-sm px-3 py-1.5 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      Create
                    </button>
                    <button
                      onClick={() => setVpnAdd(null)}
                      className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <SortableTh label="Email" sortKey="email" sort={vpnUserSort.sort} onSort={vpnUserSort.toggle} />
                      <SortableTh label="Name" sortKey="name" sort={vpnUserSort.sort} onSort={vpnUserSort.toggle} />
                      <SortableTh label="Type" sortKey="accountType" sort={vpnUserSort.sort} onSort={vpnUserSort.toggle} />
                      <SortableTh label="Admin" sortKey="isAdmin" sort={vpnUserSort.sort} onSort={vpnUserSort.toggle} />
                      <SortableTh label="Status" sortKey="enabled" sort={vpnUserSort.sort} onSort={vpnUserSort.toggle} />
                      {canManage && <th className={th}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedVpnUsers.map((u) => (
                      <React.Fragment key={u.id}>
                        <tr className={rowCls}>
                          <td className={td}>{u.email}</td>
                          <td className={td}>{u.name}</td>
                          <td className={td}>{u.accountType}</td>
                          <td className={td}>{u.isAdmin ? "yes" : ""}</td>
                          <td className={td}>
                            {u.enabled ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                                Enabled
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                                Disabled
                              </span>
                            )}
                          </td>
                          {canManage && (
                            <td className={td}>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => setVpnEnabled(u, !u.enabled)}
                                  className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                                >
                                  {u.enabled ? "Disable" : "Enable"}
                                </button>
                                <button
                                  onClick={() => {
                                    setPwUser(u.id || null);
                                    setPwValue("");
                                    setPwEmail(false);
                                  }}
                                  className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                                >
                                  Reset password
                                </button>
                                <button
                                  onClick={() => deleteVpnUser(u)}
                                  className="text-xs px-2 py-0.5 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/30"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                        {pwUser === u.id && (
                          <tr>
                            <td
                              colSpan={canManage ? 6 : 5}
                              className="bg-indigo-50/60 dark:bg-indigo-900/10 border-y border-gray-100 dark:border-gray-700/60"
                            >
                              <div className="flex flex-wrap items-center gap-2 p-2">
                                <input
                                  type="password"
                                  value={pwValue}
                                  onChange={(e) => setPwValue(e.target.value)}
                                  placeholder="New password"
                                  className="w-48 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                                />
                                <label className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                  <input type="checkbox" checked={pwEmail} onChange={(e) => setPwEmail(e.target.checked)} /> email to user
                                </label>
                                <button
                                  onClick={resetVpnPw}
                                  disabled={!pwValue}
                                  className="bg-indigo-600 text-white text-sm px-3 py-1 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                                >
                                  Set password
                                </button>
                                <button
                                  onClick={() => setPwUser(null)}
                                  className="text-sm px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg"
                                >
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    {vpnUsers.length === 0 && (
                      <tr>
                        <td colSpan={canManage ? 6 : 5} className="px-3 py-6 text-center text-gray-400">
                          No Meraki auth users (Client VPN accounts) on this network.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            )}

            {!loading && tab === "Wireless" && netId && (
              <>
                {canManage && !editSsid && (
                  <div className="p-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Wireless SSIDs
                    </h3>
                    <button
                      onClick={addSsid}
                      className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    >
                      + Add / enable SSID
                    </button>
                  </div>
                )}
                {editSsid && (
                  <div
                    ref={editorRef} className="p-3 border-b border-gray-100 dark:border-gray-700/60 bg-indigo-50/60 dark:bg-indigo-900/10 flex flex-wrap items-end gap-3">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Edit SSID {editSsid.number}
                    </div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Name
                      <input
                        value={editSsid.name}
                        onChange={(e) => setEditSsid({ ...editSsid, name: e.target.value })}
                        className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      />
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Enabled
                      <select
                        value={editSsid.enabled ? "yes" : "no"}
                        onChange={(e) => setEditSsid({ ...editSsid, enabled: e.target.value === "yes" })}
                        className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      >
                        <option value="yes">On</option>
                        <option value="no">Off</option>
                      </select>
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Auth mode
                      <select
                        value={editSsid.authMode}
                        onChange={(e) => setEditSsid({ ...editSsid, authMode: e.target.value })}
                        className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      >
                        <option value="open">open</option>
                        <option value="psk">psk</option>
                        <option value="8021x-radius">8021x-radius</option>
                      </select>
                    </label>
                    {editSsid.authMode === "psk" && (
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Passphrase
                        <input
                          value={editSsid.psk}
                          onChange={(e) => setEditSsid({ ...editSsid, psk: e.target.value })}
                          placeholder="leave blank to keep current"
                          className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                        />
                      </label>
                    )}
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Broadcast
                      <select
                        value={editSsid.visible ? "visible" : "hidden"}
                        onChange={(e) => setEditSsid({ ...editSsid, visible: e.target.value === "visible" })}
                        className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      >
                        <option value="visible">visible</option>
                        <option value="hidden">hidden</option>
                      </select>
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      IP assignment
                      <select
                        value={editSsid.ipAssignmentMode}
                        onChange={(e) => setEditSsid({ ...editSsid, ipAssignmentMode: e.target.value })}
                        className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      >
                        <option value="">(unchanged)</option>
                        <option value="NAT mode">NAT mode</option>
                        <option value="Bridge mode">Bridge mode</option>
                        <option value="Layer 3 roaming">Layer 3 roaming</option>
                      </select>
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      VLAN tagging
                      <select
                        value={editSsid.useVlanTagging ? "yes" : "no"}
                        onChange={(e) =>
                          setEditSsid({ ...editSsid, useVlanTagging: e.target.value === "yes" })
                        }
                        className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      >
                        <option value="no">off</option>
                        <option value="yes">on</option>
                      </select>
                    </label>
                    {editSsid.useVlanTagging && (
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Default VLAN
                        <input
                          value={editSsid.defaultVlanId}
                          onChange={(e) => setEditSsid({ ...editSsid, defaultVlanId: e.target.value })}
                          className="block mt-0.5 w-16 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                        />
                      </label>
                    )}
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Band
                      <select
                        value={editSsid.bandSelection}
                        onChange={(e) => setEditSsid({ ...editSsid, bandSelection: e.target.value })}
                        className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      >
                        <option value="">(unchanged)</option>
                        <option value="Dual band operation">Dual band</option>
                        <option value="5 GHz band only">5 GHz only</option>
                        <option value="Dual band operation with Band Steering">
                          Dual band + steering
                        </option>
                      </select>
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      BW up (kbps)
                      <input
                        value={editSsid.bwUp}
                        placeholder="0=unltd"
                        onChange={(e) => setEditSsid({ ...editSsid, bwUp: e.target.value })}
                        className="block mt-0.5 w-20 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                      />
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      BW down (kbps)
                      <input
                        value={editSsid.bwDown}
                        placeholder="0=unltd"
                        onChange={(e) => setEditSsid({ ...editSsid, bwDown: e.target.value })}
                        className="block mt-0.5 w-20 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                      />
                    </label>
                    <button
                      onClick={() => {
                        const fields: Record<string, unknown> = {
                          network_id: netId,
                          number: editSsid.number,
                          enabled: editSsid.enabled,
                          authMode: editSsid.authMode || undefined,
                          visible: editSsid.visible,
                          useVlanTagging: editSsid.useVlanTagging,
                        };
                        if (editSsid.name) fields.name = editSsid.name;
                        if (editSsid.authMode === "psk" && editSsid.psk) fields.psk = editSsid.psk;
                        if (editSsid.ipAssignmentMode) fields.ipAssignmentMode = editSsid.ipAssignmentMode;
                        if (editSsid.useVlanTagging && editSsid.defaultVlanId !== "")
                          fields.defaultVlanId = Number(editSsid.defaultVlanId);
                        if (editSsid.bandSelection) fields.bandSelection = editSsid.bandSelection;
                        if (editSsid.bwUp !== "") fields.perClientBandwidthLimitUp = Number(editSsid.bwUp);
                        if (editSsid.bwDown !== "")
                          fields.perClientBandwidthLimitDown = Number(editSsid.bwDown);
                        propose("meraki_set_ssid", fields);
                        setEditSsid(null);
                      }}
                      className="bg-indigo-600 text-white text-sm px-3 py-1.5 rounded-lg hover:bg-indigo-700"
                    >
                      Propose change
                    </button>
                    <button
                      onClick={() => setEditSsid(null)}
                      className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <SortableTh label="#" sortKey="number" sort={ssidSort.sort} onSort={ssidSort.toggle} />
                      <SortableTh label="SSID" sortKey="name" sort={ssidSort.sort} onSort={ssidSort.toggle} />
                      <SortableTh label="Enabled" sortKey="enabled" sort={ssidSort.sort} onSort={ssidSort.toggle} />
                      <SortableTh label="Auth" sortKey="authMode" sort={ssidSort.sort} onSort={ssidSort.toggle} />
                      <SortableTh label="Broadcast" sortKey="visible" sort={ssidSort.sort} onSort={ssidSort.toggle} />
                      {canManage && <th className={th}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSsids.map((s) => (
                      <tr
                        key={s.number}
                        className={rowCls}
                        title={`SSID ${s.number} "${s.name || ""}" — ${s.enabled ? "on" : "off"}, auth ${s.authMode}, ${s.visible === false ? "hidden" : "visible"}`}
                      >
                        <td className={td}>{s.number}</td>
                        <td className={td}>{s.name}</td>
                        <td className={td}>
                          {s.enabled ? (
                            <span className="text-green-700 dark:text-green-400">On</span>
                          ) : (
                            <span className="text-gray-400">Off</span>
                          )}
                        </td>
                        <td className={td}>{s.authMode}</td>
                        <td className={td}>{s.visible === false ? "hidden" : "visible"}</td>
                        {canManage && (
                          <td className={td}>
                            <div className="flex gap-1">
                              <button
                                onClick={() =>
                                  propose("meraki_set_ssid", {
                                    network_id: netId,
                                    number: s.number,
                                    enabled: !s.enabled,
                                  })
                                }
                                className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                              >
                                {s.enabled ? "Disable" : "Enable"}
                              </button>
                              <button
                                onClick={() =>
                                  setEditSsid({
                                    number: s.number,
                                    name: s.name || "",
                                    enabled: !!s.enabled,
                                    authMode: s.authMode || "open",
                                    psk: "",
                                    visible: s.visible !== false,
                                    ipAssignmentMode: s.ipAssignmentMode || "",
                                    useVlanTagging: !!s.useVlanTagging,
                                    defaultVlanId: s.defaultVlanId != null ? String(s.defaultVlanId) : "",
                                    bandSelection: s.bandSelection || "",
                                    bwUp:
                                      s.perClientBandwidthLimitUp != null
                                        ? String(s.perClientBandwidthLimitUp)
                                        : "",
                                    bwDown:
                                      s.perClientBandwidthLimitDown != null
                                        ? String(s.perClientBandwidthLimitDown)
                                        : "",
                                  })
                                }
                                className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                              >
                                Edit
                              </button>
                              {aiOn && (
                                <HoverSuggest
                                  niId={niId}
                                  onProposed={handleProposed}
                                  kind="ssid"
                                  networkId={netId}
                                  item={{
                                    number: s.number,
                                    name: s.name,
                                    enabled: s.enabled,
                                    authMode: s.authMode,
                                    visible: s.visible,
                                  }}
                                />
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    {ssids.length === 0 && (
                      <tr>
                        <td className="px-3 py-6 text-center text-gray-400" colSpan={canManage ? 6 : 5}>
                          No wireless SSIDs (network has no MR access points).
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            )}

            {!loading && tab === "VLANs" && netId && (
              <>
                {canManage && !editVlan && (
                  <div className="p-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">VLANs</h3>
                    <button
                      onClick={() =>
                        setEditVlan({ id: "", name: "", subnet: "", applianceIp: "", isNew: true })
                      }
                      className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    >
                      + Add VLAN
                    </button>
                  </div>
                )}
                {editVlan && (
                  <div
                    ref={editorRef} className="p-3 border-b border-gray-100 dark:border-gray-700/60 bg-indigo-50/60 dark:bg-indigo-900/10 flex flex-wrap items-end gap-3">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {editVlan.isNew ? "New VLAN" : `Edit VLAN ${editVlan.id}`}
                    </div>
                    {editVlan.isNew && (
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        VLAN ID
                        <input
                          value={editVlan.id}
                          onChange={(e) => setEditVlan({ ...editVlan, id: e.target.value })}
                          placeholder="30"
                          className="block mt-0.5 w-16 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                        />
                      </label>
                    )}
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Name
                      <input
                        value={editVlan.name}
                        onChange={(e) => setEditVlan({ ...editVlan, name: e.target.value })}
                        className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      />
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Subnet (CIDR)
                      <input
                        value={editVlan.subnet}
                        onChange={(e) => setEditVlan({ ...editVlan, subnet: e.target.value })}
                        placeholder="10.0.10.0/24"
                        className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                      />
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Appliance IP
                      <input
                        value={editVlan.applianceIp}
                        onChange={(e) => setEditVlan({ ...editVlan, applianceIp: e.target.value })}
                        placeholder="10.0.10.1"
                        className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                      />
                    </label>
                    <button
                      onClick={() => {
                        if (editVlan.isNew) {
                          propose("meraki_create_vlan", {
                            network_id: netId,
                            vlan_id: Number(editVlan.id),
                            name: editVlan.name || undefined,
                            subnet: editVlan.subnet || undefined,
                            applianceIp: editVlan.applianceIp || undefined,
                          });
                        } else {
                          propose("meraki_update_vlan", {
                            network_id: netId,
                            vlan_id: editVlan.id,
                            name: editVlan.name || undefined,
                            subnet: editVlan.subnet || undefined,
                            applianceIp: editVlan.applianceIp || undefined,
                          });
                        }
                        setEditVlan(null);
                      }}
                      disabled={editVlan.isNew && (!editVlan.id || !editVlan.subnet)}
                      className="bg-indigo-600 text-white text-sm px-3 py-1.5 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {editVlan.isNew ? "Propose new VLAN" : "Propose change"}
                    </button>
                    <button
                      onClick={() => setEditVlan(null)}
                      className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <SortableTh label="VLAN" sortKey="id" sort={vlanSort.sort} onSort={vlanSort.toggle} />
                      <SortableTh label="Name" sortKey="name" sort={vlanSort.sort} onSort={vlanSort.toggle} />
                      <SortableTh label="Subnet" sortKey="subnet" sort={vlanSort.sort} onSort={vlanSort.toggle} />
                      <SortableTh label="Appliance IP" sortKey="applianceIp" sort={vlanSort.sort} onSort={vlanSort.toggle} />
                      <SortableTh label="DHCP" sortKey="dhcpHandling" sort={vlanSort.sort} onSort={vlanSort.toggle} />
                      <th className={th} title="MX ports carrying this VLAN. U = untagged (access or trunk-native), T = tagged (trunk)">
                        Gateway ports
                      </th>
                      {canManage && <th className={th}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedVlans.map((v) => (
                      <tr
                        key={String(v.id)}
                        className={rowCls}
                        title={`VLAN ${v.id} ${v.name || ""} — ${v.subnet || ""} (MX ${v.applianceIp || ""}), DHCP ${v.dhcpHandling || ""}`}
                      >
                        <td className={td}>{v.id}</td>
                        <td className={td}>{v.name}</td>
                        <td className={`${td} font-mono text-xs`}>{v.subnet}</td>
                        <td className={`${td} font-mono text-xs`}>{v.applianceIp}</td>
                        <td className={td}>{v.dhcpHandling}</td>
                        <td className={`${td} text-xs`}>
                          {(() => {
                            const gps = gatewayPortsFor(Number(v.id));
                            return gps.length === 0
                              ? "—"
                              : gps.map((g) => (
                                  <span
                                    key={g.port}
                                    title={g.kind === "tagged" ? "tagged" : `untagged (${g.kind})`}
                                    className={`inline-block mr-1.5 font-mono ${g.kind === "tagged" ? "text-blue-600 dark:text-blue-400" : "text-green-700 dark:text-green-400"}`}
                                  >
                                    {g.port}
                                    <span className="text-[9px] align-super">
                                      {g.kind === "tagged" ? "T" : "U"}
                                    </span>
                                  </span>
                                ));
                          })()}
                        </td>
                        {canManage && (
                          <td className={td}>
                            <div className="flex gap-1">
                              <button
                                onClick={() =>
                                  setEditVlan({
                                    id: String(v.id),
                                    name: v.name || "",
                                    subnet: v.subnet || "",
                                    applianceIp: v.applianceIp || "",
                                  })
                                }
                                className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                              >
                                Edit
                              </button>
                              {aiOn && (
                                <HoverSuggest
                                  niId={niId}
                                  onProposed={handleProposed}
                                  kind="vlan"
                                  networkId={netId}
                                  item={{
                                    vlan_id: v.id,
                                    name: v.name,
                                    subnet: v.subnet,
                                    applianceIp: v.applianceIp,
                                    dhcpHandling: v.dhcpHandling,
                                  }}
                                />
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    {vlans.length === 0 && (
                      <tr>
                        <td className="px-3 py-6 text-center text-gray-400" colSpan={canManage ? 7 : 6}>
                          No VLANs (VLANs not enabled, or no MX in this network).
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="border-t border-gray-200 dark:border-gray-700">
                  <div className="p-2">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Gateway ports (MX)
                    </h3>
                    <p className="text-[11px] text-gray-400">
                      Physical ports on the security appliance and the VLAN on each.
                    </p>
                  </div>
                  {editApplPort && (
                    <div
                    ref={editorRef} className="p-3 bg-indigo-50/60 dark:bg-indigo-900/10 border-y border-gray-100 dark:border-gray-700/60 flex flex-wrap items-end gap-2">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        Port {editApplPort.port_id}
                      </div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Enabled
                        <select
                          value={editApplPort.enabled ? "yes" : "no"}
                          onChange={(e) =>
                            setEditApplPort({ ...editApplPort, enabled: e.target.value === "yes" })
                          }
                          className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                        >
                          <option value="yes">On</option>
                          <option value="no">Off</option>
                        </select>
                      </label>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Type
                        <select
                          value={editApplPort.type}
                          onChange={(e) => setEditApplPort({ ...editApplPort, type: e.target.value })}
                          className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                        >
                          <option value="access">access</option>
                          <option value="trunk">trunk</option>
                        </select>
                      </label>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        {editApplPort.type === "trunk" ? "Native VLAN" : "VLAN"}
                        <input
                          value={editApplPort.vlan}
                          onChange={(e) => setEditApplPort({ ...editApplPort, vlan: e.target.value })}
                          className="block mt-0.5 w-16 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                        />
                      </label>
                      {editApplPort.type === "trunk" && (
                        <label className="text-xs text-gray-500 dark:text-gray-400">
                          Allowed VLANs
                          <input
                            value={editApplPort.allowedVlans}
                            placeholder="all or 1,10,20"
                            onChange={(e) =>
                              setEditApplPort({ ...editApplPort, allowedVlans: e.target.value })
                            }
                            className="block mt-0.5 w-28 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                          />
                        </label>
                      )}
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Drop untagged
                        <select
                          value={editApplPort.dropUntaggedTraffic ? "yes" : "no"}
                          onChange={(e) =>
                            setEditApplPort({
                              ...editApplPort,
                              dropUntaggedTraffic: e.target.value === "yes",
                            })
                          }
                          className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                        >
                          <option value="no">no</option>
                          <option value="yes">yes</option>
                        </select>
                      </label>
                      <button
                        onClick={proposeApplPort}
                        className="bg-indigo-600 text-white text-sm px-3 py-1.5 rounded-lg hover:bg-indigo-700"
                      >
                        Propose change
                      </button>
                      <button
                        onClick={() => setEditApplPort(null)}
                        className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <th className={th}>Port</th>
                        <th className={th}>Enabled</th>
                        <th className={th}>Type</th>
                        <th className={th}>VLAN</th>
                        <th className={th}>Allowed</th>
                        <th className={th}>Drop untagged</th>
                        {canManage && <th className={th}>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {applPorts.map((p) => (
                        <tr
                          key={p.number}
                          className={rowCls}
                          title={`MX port ${p.number} — ${p.type}, VLAN ${p.vlan ?? "?"}${p.allowedVlans ? ` (allowed ${p.allowedVlans})` : ""}, ${p.enabled === false ? "disabled" : "enabled"}`}
                        >
                          <td className={td}>{p.number}</td>
                          <td className={td}>
                            {p.enabled === false ? (
                              <span className="text-gray-400">Off</span>
                            ) : (
                              <span className="text-green-700 dark:text-green-400">On</span>
                            )}
                          </td>
                          <td className={td}>{p.type}</td>
                          <td className={td}>{p.vlan}</td>
                          <td className={td}>{p.allowedVlans || ""}</td>
                          <td className={td}>{p.dropUntaggedTraffic ? "yes" : "no"}</td>
                          {canManage && (
                            <td className={td}>
                              <div className="flex gap-1">
                                <button
                                  onClick={() =>
                                    setEditApplPort({
                                      port_id: String(p.number),
                                      enabled: p.enabled !== false,
                                      type: p.type || "access",
                                      vlan: p.vlan != null ? String(p.vlan) : "",
                                      allowedVlans: p.allowedVlans || "",
                                      dropUntaggedTraffic: !!p.dropUntaggedTraffic,
                                    })
                                  }
                                  className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                                >
                                  Edit
                                </button>
                                {aiOn && (
                                  <HoverSuggest
                                    niId={niId}
                                    onProposed={handleProposed}
                                    kind="gateway-port"
                                    networkId={netId}
                                    item={{
                                      port_id: p.number,
                                      type: p.type,
                                      vlan: p.vlan,
                                      allowedVlans: p.allowedVlans,
                                    }}
                                  />
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                      {applPorts.length === 0 && (
                        <tr>
                          <td className="px-3 py-4 text-center text-gray-400" colSpan={canManage ? 7 : 6}>
                            No configurable MX ports (single-LAN mode, or no MX in this network).
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {switches.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700">
                  <div className="p-2 flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Switch ports
                    </h3>
                    {switches.length > 0 ? (
                      <select
                        value={switchSerial}
                        onChange={(e) => {
                          setSwitchSerial(e.target.value);
                          setEditPort(null);
                        }}
                        className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-sm"
                      >
                        {switches.map((s) => (
                          <option key={s.serial} value={s.serial}>
                            {s.hostname || s.serial} ({s.model})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-xs text-gray-400">No MS switches in this org.</span>
                    )}
                  </div>

                  {editPort && (
                    <div
                    ref={editorRef} className="p-3 bg-indigo-50/60 dark:bg-indigo-900/10 border-y border-gray-100 dark:border-gray-700/60 flex flex-wrap items-end gap-2">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        Port {editPort.port_id}
                      </div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Name
                        <input
                          value={editPort.name}
                          onChange={(e) => setEditPort({ ...editPort, name: e.target.value })}
                          className="block mt-0.5 w-32 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                        />
                      </label>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Enabled
                        <select
                          value={editPort.enabled ? "yes" : "no"}
                          onChange={(e) => setEditPort({ ...editPort, enabled: e.target.value === "yes" })}
                          className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                        >
                          <option value="yes">On</option>
                          <option value="no">Off</option>
                        </select>
                      </label>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Type
                        <select
                          value={editPort.type}
                          onChange={(e) => setEditPort({ ...editPort, type: e.target.value })}
                          className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                        >
                          <option value="access">access</option>
                          <option value="trunk">trunk</option>
                        </select>
                      </label>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        {editPort.type === "trunk" ? "Native VLAN" : "VLAN"}
                        <input
                          value={editPort.vlan}
                          onChange={(e) => setEditPort({ ...editPort, vlan: e.target.value })}
                          className="block mt-0.5 w-16 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                        />
                      </label>
                      {editPort.type === "trunk" && (
                        <label className="text-xs text-gray-500 dark:text-gray-400">
                          Allowed VLANs
                          <input
                            value={editPort.allowedVlans}
                            placeholder="all or 1,10,20"
                            onChange={(e) => setEditPort({ ...editPort, allowedVlans: e.target.value })}
                            className="block mt-0.5 w-28 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                          />
                        </label>
                      )}
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Voice VLAN
                        <input
                          value={editPort.voiceVlan}
                          onChange={(e) => setEditPort({ ...editPort, voiceVlan: e.target.value })}
                          className="block mt-0.5 w-16 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                        />
                      </label>
                      <button
                        onClick={proposePort}
                        className="bg-indigo-600 text-white text-sm px-3 py-1.5 rounded-lg hover:bg-indigo-700"
                      >
                        Propose change
                      </button>
                      <button
                        onClick={() => setEditPort(null)}
                        className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg"
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  {switchSerial && (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-900/50">
                        <tr>
                          <th className={th}>Port</th>
                          <th className={th}>Name</th>
                          <th className={th}>Enabled</th>
                          <th className={th}>Type</th>
                          <th className={th}>VLAN</th>
                          <th className={th}>Allowed</th>
                          <th className={th}>PoE</th>
                          {canManage && <th className={th}>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {switchPorts.map((p) => (
                          <tr
                            key={p.portId}
                            className={rowCls}
                            title={`Port ${p.portId} ${p.name || ""} — ${p.type}, VLAN ${p.vlan ?? "?"}${p.allowedVlans ? ` (allowed ${p.allowedVlans})` : ""}, ${p.enabled === false ? "disabled" : "enabled"}`}
                          >
                            <td className={td}>{p.portId}</td>
                            <td className={td}>{p.name}</td>
                            <td className={td}>
                              {p.enabled === false ? (
                                <span className="text-gray-400">Off</span>
                              ) : (
                                <span className="text-green-700 dark:text-green-400">On</span>
                              )}
                            </td>
                            <td className={td}>{p.type}</td>
                            <td className={td}>{p.vlan}</td>
                            <td className={td}>{p.allowedVlans || ""}</td>
                            <td className={td}>{p.poeEnabled === false ? "off" : "on"}</td>
                            {canManage && (
                              <td className={td}>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() =>
                                      setEditPort({
                                        port_id: String(p.portId),
                                        name: p.name || "",
                                        enabled: p.enabled !== false,
                                        type: p.type || "access",
                                        vlan: p.vlan != null ? String(p.vlan) : "",
                                        voiceVlan: p.voiceVlan != null ? String(p.voiceVlan) : "",
                                        allowedVlans: p.allowedVlans || "",
                                      })
                                    }
                                    className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                                  >
                                    Edit
                                  </button>
                                  {aiOn && (
                                    <HoverSuggest
                                      niId={niId}
                                      onProposed={handleProposed}
                                      kind="switchport"
                                      item={{
                                        serial: switchSerial,
                                        port_id: p.portId,
                                        name: p.name,
                                        type: p.type,
                                        vlan: p.vlan,
                                        allowedVlans: p.allowedVlans,
                                      }}
                                    />
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                        {switchPorts.length === 0 && (
                          <tr>
                            <td className="px-3 py-4 text-center text-gray-400" colSpan={canManage ? 8 : 7}>
                              {switchSerial ? "No ports (or switch offline)." : "Select a switch."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
                )}
              </>
            )}

            {!loading && tab === "Firewall" && netId && (
              <>
                {canManage && (
                  <div className="p-2 border-b border-gray-100 dark:border-gray-700/60 flex items-center justify-end gap-2">
                    {fwEdit === null ? (
                      <button
                        onClick={startFwEdit}
                        className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                      >
                        Edit rules
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={addFwRule}
                          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                        >
                          + Add rule
                        </button>
                        <button
                          onClick={proposeFwRules}
                          className="text-xs px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                        >
                          Propose changes
                        </button>
                        <button
                          onClick={() => setFwEdit(null)}
                          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                )}

                {fwEdit === null ? (
                  <>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <SortableTh label="#" sortKey="rule" sort={fwSort.sort} onSort={fwSort.toggle} />
                        <SortableTh label="Policy" sortKey="policy" sort={fwSort.sort} onSort={fwSort.toggle} />
                        <SortableTh label="Protocol" sortKey="protocol" sort={fwSort.sort} onSort={fwSort.toggle} />
                        <SortableTh label="Source" sortKey="source" sort={fwSort.sort} onSort={fwSort.toggle} />
                        <SortableTh label="Destination" sortKey="destination" sort={fwSort.sort} onSort={fwSort.toggle} />
                        <SortableTh label="Comment" sortKey="comment" sort={fwSort.sort} onSort={fwSort.toggle} />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFw.map((r) => (
                        <tr
                          key={r.rule}
                          className={rowCls}
                          title={`Rule ${r.rule} (MX evaluation order) — ${r.policy} ${r.protocol}: src ${r.srcCidr}:${r.srcPort} → dst ${r.destCidr}:${r.destPort}${r.comment ? ` (${r.comment})` : ""}`}
                        >
                          <td className={td}>{r.rule}</td>
                          <td className={td}>
                            <span
                              className={
                                (r.policy || "").toLowerCase() === "deny"
                                  ? "text-red-600 dark:text-red-400"
                                  : "text-green-700 dark:text-green-400"
                              }
                            >
                              {r.policy}
                            </span>
                          </td>
                          <td className={td}>{r.protocol}</td>
                          <td className={`${td} font-mono text-xs`}>
                            {r.srcCidr}
                            {r.srcPort && r.srcPort !== "Any" ? `:${r.srcPort}` : ""}
                          </td>
                          <td className={`${td} font-mono text-xs`}>
                            {r.destCidr}
                            {r.destPort && r.destPort !== "Any" ? `:${r.destPort}` : ""}
                          </td>
                          <td className={td}>{r.comment}</td>
                        </tr>
                      ))}
                      {fw.length === 0 && (
                        <tr>
                          <td className="px-3 py-6 text-center text-gray-400" colSpan={6}>
                            No custom L3 firewall rules (default allow).
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {/* Firewall rules are evaluated top-down, so say so plainly
                      when the view is sorted some other way — the "#" column
                      still shows each rule's real position. */}
                  {fwSort.sort.key !== "rule" && fw.length > 1 && (
                    <p className="px-3 py-2 text-[11px] text-amber-700 dark:text-amber-400 border-t border-gray-100 dark:border-gray-700/60">
                      Sorted by {fwSort.sort.key} — this is <strong>not</strong> the order the MX
                      evaluates rules in. The <strong>#</strong> column shows each rule's real
                      position; sort by <strong>#</strong> to put the list back in evaluation order.
                    </p>
                  )}
                  </>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <th className={th}>#</th>
                        <th className={th}>Policy</th>
                        <th className={th}>Proto</th>
                        <th className={th}>Src CIDR</th>
                        <th className={th}>Src port</th>
                        <th className={th}>Dst CIDR</th>
                        <th className={th}>Dst port</th>
                        <th className={th}>Comment</th>
                        <th className={th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {fwEdit.map((r, i) => (
                        <tr key={i} className={rowCls}>
                          <td className={td}>{i + 1}</td>
                          <td className={td}>
                            <select
                              value={r.policy || "deny"}
                              onChange={(e) => updateFwRule(i, { policy: e.target.value })}
                              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs"
                            >
                              <option value="allow">allow</option>
                              <option value="deny">deny</option>
                            </select>
                          </td>
                          <td className={td}>
                            <select
                              value={r.protocol || "any"}
                              onChange={(e) => updateFwRule(i, { protocol: e.target.value })}
                              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs"
                            >
                              <option value="any">any</option>
                              <option value="tcp">tcp</option>
                              <option value="udp">udp</option>
                              <option value="icmp">icmp</option>
                            </select>
                          </td>
                          <td className={td}>
                            <input
                              value={r.srcCidr || ""}
                              placeholder="Any"
                              onChange={(e) => updateFwRule(i, { srcCidr: e.target.value })}
                              className="w-28 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs font-mono"
                            />
                          </td>
                          <td className={td}>
                            <input
                              value={r.srcPort || ""}
                              placeholder="Any"
                              onChange={(e) => updateFwRule(i, { srcPort: e.target.value })}
                              className="w-16 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs font-mono"
                            />
                          </td>
                          <td className={td}>
                            <input
                              value={r.destCidr || ""}
                              placeholder="Any"
                              onChange={(e) => updateFwRule(i, { destCidr: e.target.value })}
                              className="w-28 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs font-mono"
                            />
                          </td>
                          <td className={td}>
                            <input
                              value={r.destPort || ""}
                              placeholder="Any"
                              onChange={(e) => updateFwRule(i, { destPort: e.target.value })}
                              className="w-16 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs font-mono"
                            />
                          </td>
                          <td className={td}>
                            <input
                              value={r.comment || ""}
                              onChange={(e) => updateFwRule(i, { comment: e.target.value })}
                              className="w-32 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs"
                            />
                          </td>
                          <td className={td}>
                            <div className="flex gap-1">
                              <button
                                onClick={() => moveFwRule(i, -1)}
                                disabled={i === 0}
                                className="text-xs px-1 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-30"
                                title="Move up"
                              >
                                ↑
                              </button>
                              <button
                                onClick={() => moveFwRule(i, 1)}
                                disabled={i === fwEdit.length - 1}
                                className="text-xs px-1 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-30"
                                title="Move down"
                              >
                                ↓
                              </button>
                              <button
                                onClick={() => removeFwRule(i)}
                                className="text-xs px-1 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded"
                                title="Delete rule"
                              >
                                ✕
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {fwEdit.length === 0 && (
                        <tr>
                          <td className="px-3 py-6 text-center text-gray-400" colSpan={9}>
                            No rules — the default allow-any applies. Add a rule above.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
                <p className="text-xs text-gray-400 px-3 py-2 border-t border-gray-100 dark:border-gray-700/60">
                  Rules are evaluated top-down. Meraki applies a default allow-any rule last
                  automatically (not shown/editable). Use ↑/↓ to reorder; “Propose changes” replaces
                  the whole ruleset and waits for your approval.
                </p>

                <div className="border-t border-gray-200 dark:border-gray-700">
                  <div className="p-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Port forwarding
                    </h3>
                    {canManage && editPf === null && (
                      <button
                        onClick={() =>
                          setEditPf({
                            name: "",
                            protocol: "tcp",
                            publicPort: "",
                            localIp: "",
                            localPort: "",
                            uplink: "internet1",
                          })
                        }
                        className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                      >
                        + Add rule
                      </button>
                    )}
                  </div>
                  {editPf && (
                    <div className="p-3 bg-indigo-50/60 dark:bg-indigo-900/10 border-y border-gray-100 dark:border-gray-700/60 flex flex-wrap items-end gap-2">
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Name
                        <input
                          value={editPf.name}
                          onChange={(e) => setEditPf({ ...editPf, name: e.target.value })}
                          className="block mt-0.5 w-32 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                        />
                      </label>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Protocol
                        <select
                          value={editPf.protocol}
                          onChange={(e) => setEditPf({ ...editPf, protocol: e.target.value })}
                          className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                        >
                          <option value="tcp">tcp</option>
                          <option value="udp">udp</option>
                        </select>
                      </label>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Public port
                        <input
                          value={editPf.publicPort}
                          placeholder="8080"
                          onChange={(e) => setEditPf({ ...editPf, publicPort: e.target.value })}
                          className="block mt-0.5 w-20 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                        />
                      </label>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Local IP
                        <input
                          value={editPf.localIp}
                          placeholder="10.0.0.10"
                          onChange={(e) => setEditPf({ ...editPf, localIp: e.target.value })}
                          className="block mt-0.5 w-28 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                        />
                      </label>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Local port
                        <input
                          value={editPf.localPort}
                          placeholder="80"
                          onChange={(e) => setEditPf({ ...editPf, localPort: e.target.value })}
                          className="block mt-0.5 w-20 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                        />
                      </label>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Uplink
                        <select
                          value={editPf.uplink}
                          onChange={(e) => setEditPf({ ...editPf, uplink: e.target.value })}
                          className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                        >
                          <option value="internet1">internet1</option>
                          <option value="internet2">internet2</option>
                          <option value="both">both</option>
                        </select>
                      </label>
                      <button
                        onClick={proposePf}
                        className="bg-indigo-600 text-white text-sm px-3 py-1.5 rounded-lg hover:bg-indigo-700"
                      >
                        Propose rule
                      </button>
                      <button
                        onClick={() => setEditPf(null)}
                        className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <SortableTh label="Name" sortKey="name" sort={pfSort.sort} onSort={pfSort.toggle} />
                        <SortableTh label="Proto" sortKey="protocol" sort={pfSort.sort} onSort={pfSort.toggle} />
                        <SortableTh label="Public" sortKey="publicPort" sort={pfSort.sort} onSort={pfSort.toggle} />
                        <SortableTh label="Local" sortKey="lanIp" sort={pfSort.sort} onSort={pfSort.toggle} />
                        <SortableTh label="Uplink" sortKey="uplink" sort={pfSort.sort} onSort={pfSort.toggle} />
                        <SortableTh label="Allowed" sortKey="allowed" sort={pfSort.sort} onSort={pfSort.toggle} />
                        {canManage && <th className={th}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPf.map((r, i) => (
                        <tr
                          key={i}
                          className={rowCls}
                          title={`${r.name || ""} — ${r.protocol}/${r.publicPort} → ${r.lanIp}:${r.localPort} via ${r.uplink}`}
                        >
                          <td className={td}>{r.name}</td>
                          <td className={td}>{r.protocol}</td>
                          <td className={td}>{r.publicPort}</td>
                          <td className={`${td} font-mono text-xs`}>
                            {r.lanIp}:{r.localPort}
                          </td>
                          <td className={td}>{r.uplink}</td>
                          <td className={td}>{(r.allowedIps || []).join(", ") || "any"}</td>
                          {canManage && (
                            <td className={td}>
                              <div className="flex gap-1">
                                <button
                                  onClick={() =>
                                    setEditPf({
                                      name: r.name || "",
                                      protocol: r.protocol || "tcp",
                                      publicPort: r.publicPort || "",
                                      localIp: r.lanIp || "",
                                      localPort: r.localPort || "",
                                      uplink: r.uplink || "internet1",
                                    })
                                  }
                                  className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                                >
                                  Edit
                                </button>
                                {aiOn && (
                                  <HoverSuggest
                                    niId={niId}
                                    onProposed={handleProposed}
                                    kind="port-forward"
                                    networkId={netId}
                                    item={{
                                      name: r.name,
                                      protocol: r.protocol,
                                      publicPort: r.publicPort,
                                      lanIp: r.lanIp,
                                      localPort: r.localPort,
                                    }}
                                  />
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                      {pf.length === 0 && (
                        <tr>
                          <td className="px-3 py-4 text-center text-gray-400" colSpan={canManage ? 7 : 6}>
                            No port-forwarding rules.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700">
                  <div className="p-2">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">NAT</h3>
                    <p className="text-[11px] text-gray-400">
                      1:1 and 1:Many NAT on the MX. Editing replaces the whole list for that type
                      and waits for your approval.
                    </p>
                  </div>

                  {/* 1:1 NAT */}
                  <div className="px-2 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                      1:1 NAT
                    </span>
                    {canManage &&
                      (nat1to1Edit === null ? (
                        <button
                          onClick={start1to1Edit}
                          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                        >
                          Edit
                        </button>
                      ) : (
                        <span className="flex gap-1">
                          <button
                            onClick={() =>
                              setNat1to1Edit((p) => [
                                ...(p || []),
                                { name: "", publicIp: "", lanIp: "", uplink: "internet1", protocol: "any", ports: "any", allowedIps: "any" },
                              ])
                            }
                            className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded"
                          >
                            + Add
                          </button>
                          <button
                            onClick={propose1to1}
                            className="text-xs px-3 py-1 bg-indigo-600 text-white rounded"
                          >
                            Propose
                          </button>
                          <button
                            onClick={() => setNat1to1Edit(null)}
                            className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded"
                          >
                            Cancel
                          </button>
                        </span>
                      ))}
                  </div>
                  {nat1to1Edit === null ? (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-900/50">
                        <tr>
                          <SortableTh label="Name" sortKey="name" sort={nat11Sort.sort} onSort={nat11Sort.toggle} />
                          <SortableTh label="Public IP" sortKey="publicIp" sort={nat11Sort.sort} onSort={nat11Sort.toggle} />
                          <SortableTh label="→ LAN IP" sortKey="lanIp" sort={nat11Sort.sort} onSort={nat11Sort.toggle} />
                          <SortableTh label="Uplink" sortKey="uplink" sort={nat11Sort.sort} onSort={nat11Sort.toggle} />
                          <SortableTh label="Allowed inbound" sortKey="allowedInbound" sort={nat11Sort.sort} onSort={nat11Sort.toggle} />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedNat11.map((r, i) => (
                          <tr key={i} className={rowCls}>
                            <td className={td}>{r.name}</td>
                            <td className={`${td} font-mono text-xs`}>{r.publicIp}</td>
                            <td className={`${td} font-mono text-xs`}>{r.lanIp}</td>
                            <td className={td}>{r.uplink}</td>
                            <td className={`${td} text-xs`}>
                              {(r.allowedInbound || [])
                                .map(
                                  (a) =>
                                    `${a.protocol} ${(a.destinationPorts || []).join("/")} from ${(a.allowedIps || []).join(",")}`,
                                )
                                .join("; ") || "none"}
                            </td>
                          </tr>
                        ))}
                        {nat.oneToOne.length === 0 && (
                          <tr>
                            <td className="px-3 py-3 text-center text-gray-400" colSpan={5}>
                              No 1:1 NAT rules.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-900/50">
                        <tr>
                          <th className={th}>Name</th>
                          <th className={th}>Public IP</th>
                          <th className={th}>LAN IP</th>
                          <th className={th}>Uplink</th>
                          <th className={th}>Proto</th>
                          <th className={th}>Ports</th>
                          <th className={th}>Allowed IPs</th>
                          <th className={th}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {nat1to1Edit.map((r, i) => (
                          <tr key={i} className={rowCls}>
                            <td className={td}>
                              <input value={r.name} onChange={(e) => upd1to1(i, { name: e.target.value })} className="w-24 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs" />
                            </td>
                            <td className={td}>
                              <input value={r.publicIp} onChange={(e) => upd1to1(i, { publicIp: e.target.value })} className="w-28 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs font-mono" />
                            </td>
                            <td className={td}>
                              <input value={r.lanIp} onChange={(e) => upd1to1(i, { lanIp: e.target.value })} className="w-28 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs font-mono" />
                            </td>
                            <td className={td}>
                              <select value={r.uplink} onChange={(e) => upd1to1(i, { uplink: e.target.value })} className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs">
                                <option value="internet1">internet1</option>
                                <option value="internet2">internet2</option>
                              </select>
                            </td>
                            <td className={td}>
                              <select value={r.protocol} onChange={(e) => upd1to1(i, { protocol: e.target.value })} className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs">
                                <option value="any">any</option>
                                <option value="tcp">tcp</option>
                                <option value="udp">udp</option>
                                <option value="icmp-ping">icmp-ping</option>
                              </select>
                            </td>
                            <td className={td}>
                              <input value={r.ports} placeholder="any or 80,443" onChange={(e) => upd1to1(i, { ports: e.target.value })} className="w-24 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs font-mono" />
                            </td>
                            <td className={td}>
                              <input value={r.allowedIps} placeholder="any or CIDRs" onChange={(e) => upd1to1(i, { allowedIps: e.target.value })} className="w-28 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs font-mono" />
                            </td>
                            <td className={td}>
                              <button onClick={() => setNat1to1Edit((p) => (p || []).filter((_, idx) => idx !== i))} className="text-xs px-1 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded" title="Delete rule">
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                        {nat1to1Edit.length === 0 && (
                          <tr>
                            <td className="px-3 py-3 text-center text-gray-400" colSpan={8}>
                              No rules — add one above (empty list clears all 1:1 NAT).
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}

                  {/* 1:Many NAT */}
                  <div className="px-2 pt-3 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                      1:Many NAT
                    </span>
                    {canManage &&
                      (nat1manyEdit === null ? (
                        <button
                          onClick={start1manyEdit}
                          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                        >
                          Edit
                        </button>
                      ) : (
                        <span className="flex gap-1">
                          <button
                            onClick={() =>
                              setNat1manyEdit((p) => [
                                ...(p || []),
                                { publicIp: "", uplink: "internet1", name: "", protocol: "tcp", publicPort: "", localIp: "", localPort: "", allowedIps: "any" },
                              ])
                            }
                            className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded"
                          >
                            + Add
                          </button>
                          <button
                            onClick={propose1many}
                            className="text-xs px-3 py-1 bg-indigo-600 text-white rounded"
                          >
                            Propose
                          </button>
                          <button
                            onClick={() => setNat1manyEdit(null)}
                            className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded"
                          >
                            Cancel
                          </button>
                        </span>
                      ))}
                  </div>
                  {nat1manyEdit === null ? (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-900/50">
                        <tr>
                          <SortableTh label="Public IP" sortKey="publicIp" sort={nat1mSort.sort} onSort={nat1mSort.toggle} />
                          <SortableTh label="Uplink" sortKey="uplink" sort={nat1mSort.sort} onSort={nat1mSort.toggle} />
                          <SortableTh label="Port rules" sortKey="portRules" sort={nat1mSort.sort} onSort={nat1mSort.toggle} />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedNat1m.map((r, i) => (
                          <tr key={i} className={rowCls}>
                            <td className={`${td} font-mono text-xs`}>{r.publicIp}</td>
                            <td className={td}>{r.uplink}</td>
                            <td className={`${td} text-xs`}>
                              {(r.portRules || [])
                                .map((pr) => `${pr.protocol}/${pr.publicPort}→${pr.localIp}:${pr.localPort}`)
                                .join("; ")}
                            </td>
                          </tr>
                        ))}
                        {nat.oneToMany.length === 0 && (
                          <tr>
                            <td className="px-3 py-3 text-center text-gray-400" colSpan={3}>
                              No 1:Many NAT rules.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-900/50">
                        <tr>
                          <th className={th}>Public IP</th>
                          <th className={th}>Uplink</th>
                          <th className={th}>Name</th>
                          <th className={th}>Proto</th>
                          <th className={th}>Pub port</th>
                          <th className={th}>LAN IP</th>
                          <th className={th}>LAN port</th>
                          <th className={th}>Allowed IPs</th>
                          <th className={th}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {nat1manyEdit.map((r, i) => (
                          <tr key={i} className={rowCls}>
                            <td className={td}>
                              <input value={r.publicIp} onChange={(e) => upd1many(i, { publicIp: e.target.value })} className="w-28 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs font-mono" />
                            </td>
                            <td className={td}>
                              <select value={r.uplink} onChange={(e) => upd1many(i, { uplink: e.target.value })} className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs">
                                <option value="internet1">internet1</option>
                                <option value="internet2">internet2</option>
                              </select>
                            </td>
                            <td className={td}>
                              <input value={r.name} onChange={(e) => upd1many(i, { name: e.target.value })} className="w-20 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs" />
                            </td>
                            <td className={td}>
                              <select value={r.protocol} onChange={(e) => upd1many(i, { protocol: e.target.value })} className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs">
                                <option value="tcp">tcp</option>
                                <option value="udp">udp</option>
                              </select>
                            </td>
                            <td className={td}>
                              <input value={r.publicPort} onChange={(e) => upd1many(i, { publicPort: e.target.value })} className="w-16 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs font-mono" />
                            </td>
                            <td className={td}>
                              <input value={r.localIp} onChange={(e) => upd1many(i, { localIp: e.target.value })} className="w-28 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs font-mono" />
                            </td>
                            <td className={td}>
                              <input value={r.localPort} onChange={(e) => upd1many(i, { localPort: e.target.value })} className="w-16 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs font-mono" />
                            </td>
                            <td className={td}>
                              <input value={r.allowedIps} placeholder="any" onChange={(e) => upd1many(i, { allowedIps: e.target.value })} className="w-24 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 text-xs font-mono" />
                            </td>
                            <td className={td}>
                              <button onClick={() => setNat1manyEdit((p) => (p || []).filter((_, idx) => idx !== i))} className="text-xs px-1 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded" title="Delete rule">
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                        {nat1manyEdit.length === 0 && (
                          <tr>
                            <td className="px-3 py-3 text-center text-gray-400" colSpan={9}>
                              No rules — add one above (empty list clears all 1:Many NAT).
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

            {!loading && tab === "Routing" && netId && (
              <>
                {canManage && (
                  <div className="p-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Static routes
                    </h3>
                    {editRoute === null && (
                      <button
                        onClick={() =>
                          setEditRoute({ name: "", subnet: "", gatewayIp: "", enabled: true })
                        }
                        className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                      >
                        + Add route
                      </button>
                    )}
                  </div>
                )}
                {editRoute && (
                  <div
                    ref={editorRef} className="p-3 bg-indigo-50/60 dark:bg-indigo-900/10 border-y border-gray-100 dark:border-gray-700/60 flex flex-wrap items-end gap-2">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {editRoute.id ? "Edit route" : "New static route"}
                    </div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Name
                      <input
                        value={editRoute.name}
                        onChange={(e) => setEditRoute({ ...editRoute, name: e.target.value })}
                        className="block mt-0.5 w-32 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      />
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Subnet (CIDR)
                      <input
                        value={editRoute.subnet}
                        placeholder="192.168.99.0/24"
                        onChange={(e) => setEditRoute({ ...editRoute, subnet: e.target.value })}
                        className="block mt-0.5 w-36 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                      />
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Gateway IP
                      <input
                        value={editRoute.gatewayIp}
                        placeholder="192.168.51.1"
                        onChange={(e) => setEditRoute({ ...editRoute, gatewayIp: e.target.value })}
                        className="block mt-0.5 w-32 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                      />
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      Enabled
                      <select
                        value={editRoute.enabled ? "yes" : "no"}
                        onChange={(e) => setEditRoute({ ...editRoute, enabled: e.target.value === "yes" })}
                        className="block mt-0.5 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                      >
                        <option value="yes">On</option>
                        <option value="no">Off</option>
                      </select>
                    </label>
                    <button
                      onClick={proposeRoute}
                      className="bg-indigo-600 text-white text-sm px-3 py-1.5 rounded-lg hover:bg-indigo-700"
                    >
                      Propose
                    </button>
                    <button
                      onClick={() => setEditRoute(null)}
                      className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <SortableTh label="Name" sortKey="name" sort={routeSort.sort} onSort={routeSort.toggle} />
                      <SortableTh label="Subnet" sortKey="subnet" sort={routeSort.sort} onSort={routeSort.toggle} />
                      <SortableTh label="Gateway" sortKey="gatewayIp" sort={routeSort.sort} onSort={routeSort.toggle} />
                      <SortableTh label="Enabled" sortKey="enabled" sort={routeSort.sort} onSort={routeSort.toggle} />
                      {canManage && <th className={th}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRoutes.map((r) => (
                      <tr
                        key={r.id}
                        className={rowCls}
                        title={`${r.name || ""} — ${r.subnet} via ${r.gatewayIp}`}
                      >
                        <td className={td}>{r.name}</td>
                        <td className={`${td} font-mono text-xs`}>{r.subnet}</td>
                        <td className={`${td} font-mono text-xs`}>{r.gatewayIp}</td>
                        <td className={td}>
                          {r.enabled === false ? (
                            <span className="text-gray-400">Off</span>
                          ) : (
                            <span className="text-green-700 dark:text-green-400">On</span>
                          )}
                        </td>
                        {canManage && (
                          <td className={td}>
                            <div className="flex gap-1">
                              <button
                                onClick={() =>
                                  setEditRoute({
                                    id: r.id,
                                    name: r.name || "",
                                    subnet: r.subnet || "",
                                    gatewayIp: r.gatewayIp || "",
                                    enabled: r.enabled !== false,
                                  })
                                }
                                className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() =>
                                  propose("meraki_delete_static_route", {
                                    network_id: netId,
                                    static_route_id: r.id,
                                  })
                                }
                                className="text-xs px-2 py-0.5 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/30"
                              >
                                Delete
                              </button>
                              {aiOn && (
                                <HoverSuggest
                                  niId={niId}
                                  onProposed={handleProposed}
                                  kind="static-route"
                                  networkId={netId}
                                  item={{
                                    static_route_id: r.id,
                                    name: r.name,
                                    subnet: r.subnet,
                                    gatewayIp: r.gatewayIp,
                                  }}
                                />
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    {routes.length === 0 && (
                      <tr>
                        <td className="px-3 py-4 text-center text-gray-400" colSpan={canManage ? 5 : 4}>
                          No static routes on this network's MX.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            )}

            {!loading && tab === "Group Policies" && netId && (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <SortableTh label="Name" sortKey="name" sort={gpSort.sort} onSort={gpSort.toggle} />
                    <SortableTh label="Bandwidth ↓" sortKey="limitDown" sort={gpSort.sort} onSort={gpSort.toggle} />
                    <SortableTh label="Bandwidth ↑" sortKey="limitUp" sort={gpSort.sort} onSort={gpSort.toggle} />
                    <SortableTh label="L3 rules" sortKey="l3FirewallRuleCount" sort={gpSort.sort} onSort={gpSort.toggle} />
                    <SortableTh label="Splash" sortKey="splashAuthSettings" sort={gpSort.sort} onSort={gpSort.toggle} />
                  </tr>
                </thead>
                <tbody>
                  {sortedGroupPolicies.map((g) => (
                    <tr key={g.groupPolicyId} className={rowCls}>
                      <td className={td}>{g.name}</td>
                      <td className={td}>
                        {g.limitDown != null ? `${g.limitDown} kbps` : "unlimited"}
                      </td>
                      <td className={td}>{g.limitUp != null ? `${g.limitUp} kbps` : "unlimited"}</td>
                      <td className={td}>{g.l3FirewallRuleCount ?? 0}</td>
                      <td className={td}>{g.splashAuthSettings || "—"}</td>
                    </tr>
                  ))}
                  {groupPolicies.length === 0 && (
                    <tr>
                      <td className="px-3 py-6 text-center text-gray-400" colSpan={5}>
                        No group policies on this network.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {!loading && tab === "Licenses" && (
              <>
                {licDaysUntil != null && licDaysUntil <= 30 && (
                  <div
                    className={`px-4 py-2 text-sm font-medium border-b ${
                      licDaysUntil <= 7 || licDaysUntil < 0
                        ? "bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800/60"
                        : "bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800/60"
                    }`}
                  >
                    ⚠ Meraki license{" "}
                    {licDaysUntil < 0
                      ? `expired ${-licDaysUntil} day(s) ago`
                      : `expires in ${licDaysUntil} day(s)`}
                    . Renew soon to avoid loss of features. (Monitoring also alerts at 30 and 7
                    days.)
                  </div>
                )}
                {licenseOverview && (
                  <div className="p-3 border-b border-gray-100 dark:border-gray-700/60">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-200">
                        {licenseOverview.model === "co-term"
                          ? "Co-termination license"
                          : licenseOverview.model === "per-device"
                            ? "Per-device licensing"
                            : "License"}
                      </span>
                      {licenseOverview.status && statusPill(licenseOverview.status)}
                      {licenseOverview.expirationDate && (
                        <span className="text-gray-600 dark:text-gray-300">
                          expires{" "}
                          <strong>
                            {formatDate(licenseOverview.expirationDate)}
                          </strong>
                          {licenseOverview.model === "co-term" ? " — all devices" : ""}
                        </span>
                      )}
                    </div>
                    {licenseOverview.licensedDeviceCounts &&
                      Object.keys(licenseOverview.licensedDeviceCounts).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {Object.entries(licenseOverview.licensedDeviceCounts).map(([model, n]) => (
                            <span
                              key={model}
                              className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                            >
                              {model} ×{n}
                            </span>
                          ))}
                        </div>
                      )}
                    {licenseOverview.licenses && licenseOverview.licenses.length > 0 && (
                      <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                        <div className="font-medium mb-1">License line items</div>
                        <ul className="space-y-0.5">
                          {licenseOverview.licenses.map((l, i) => (
                            <li key={i} className={l.expired ? "text-gray-400" : ""}>
                              {l.counts && l.counts.length
                                ? l.counts.map((c) => `${c.model}×${c.count}`).join(", ")
                                : l.device_serial || "—"}
                              {" — "}
                              {(l.editions || []).filter(Boolean).join("/") || "Enterprise"}
                              {l.mode ? ` (${l.mode})` : ""}
                              {l.expirationDate
                                ? ` · expires ${formatDate(l.expirationDate)}`
                                : ""}
                              {l.expired ? " · rolled/expired" : ""}
                              {l.key ? ` · …${l.key}` : ""}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {canManage && (
                  <div className="p-3 border-b border-gray-100 dark:border-gray-700/60 space-y-3">
                    <div>
                      <div className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                        Claim a license
                      </div>
                      <div className="mt-1 flex flex-wrap items-end gap-2">
                        <input
                          value={claimKey}
                          onChange={(e) => setClaimKey(e.target.value)}
                          placeholder="License key (e.g. Z2XX-XXXX-XXXX)"
                          className="w-64 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                        />
                        <select
                          value={claimMode}
                          onChange={(e) => setClaimMode(e.target.value)}
                          className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                        >
                          <option value="addDevices">add devices</option>
                          <option value="renew">renew</option>
                        </select>
                        <button
                          onClick={() => {
                            if (claimKey.trim()) {
                              propose("meraki_claim_license", {
                                license_key: claimKey.trim(),
                                mode: claimMode,
                              });
                              setClaimKey("");
                            }
                          }}
                          disabled={!claimKey.trim()}
                          className="bg-indigo-600 text-white text-sm px-3 py-1.5 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                        >
                          Claim
                        </button>
                      </div>
                      <p className="text-[11px] text-gray-400 mt-1">
                        Enter the key from your Meraki order. Co-term: "renew" extends the shared
                        expiration; "add devices" adds capacity.
                      </p>
                    </div>
                    {licenseOverview?.model === "per-device" && (
                      <div>
                        <div className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                          Move a license to another org
                        </div>
                        <div className="mt-1 flex flex-wrap items-end gap-2">
                          <select
                            value={moveLicId}
                            onChange={(e) => setMoveLicId(e.target.value)}
                            className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                          >
                            <option value="">Select license…</option>
                            {(licenseOverview.licenses || [])
                              .filter((l) => l.id)
                              .map((l) => (
                                <option key={l.id} value={l.id as string}>
                                  {(l.editions || []).filter(Boolean).join("/") || "license"}
                                  {l.device_serial ? ` · ${l.device_serial}` : ""} · …{l.key}
                                </option>
                              ))}
                          </select>
                          <input
                            value={moveDest}
                            onChange={(e) => setMoveDest(e.target.value)}
                            placeholder="Destination org ID"
                            className="w-40 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                          />
                          <button
                            onClick={() => {
                              if (moveLicId && moveDest.trim()) {
                                propose("meraki_move_license", {
                                  license_id: moveLicId,
                                  dest_org_id: moveDest.trim(),
                                });
                                setMoveLicId("");
                                setMoveDest("");
                              }
                            }}
                            disabled={!moveLicId || !moveDest.trim()}
                            className="border border-gray-300 dark:border-gray-600 text-sm px-3 py-1.5 rounded-lg disabled:opacity-50"
                          >
                            Move
                          </button>
                        </div>
                      </div>
                    )}
                    {licenseOverview?.model === "co-term" && (
                      <p className="text-[11px] text-gray-400">
                        Co-term license org-to-org moves are done in the Meraki Dashboard (the API
                        doesn't expose the full key the move needs).
                      </p>
                    )}
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <SortableTh label="Edition" sortKey="edition" sort={licSort.sort} onSort={licSort.toggle} />
                      <SortableTh label="Device / scope" sortKey="device_serial" sort={licSort.sort} onSort={licSort.toggle} />
                      <SortableTh label="State" sortKey="state" sort={licSort.sort} onSort={licSort.toggle} />
                      <SortableTh label="Expires" sortKey="expiration_date" sort={licSort.sort} onSort={licSort.toggle} />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLicenses.map((l) => (
                      <tr key={l.license_id} className={rowCls}>
                        <td className={td}>{l.edition || "—"}</td>
                        <td className={`${td} font-mono text-xs`}>
                          {l.device_serial || "org-wide (co-term)"}
                        </td>
                        <td className={td}>{statusPill(l.state || undefined)}</td>
                        <td className={td}>{expiryCell(l.days_until_expiry, l.expiration_date)}</td>
                      </tr>
                    ))}
                    {licenses.length === 0 && (
                      <tr>
                        <td className="px-3 py-6 text-center text-gray-400" colSpan={4}>
                          No license data synced yet. It syncs daily; you can also sync from the
                          customer's Meraki licenses card.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <p className="text-xs text-gray-400 px-3 py-2 border-t border-gray-100 dark:border-gray-700/60">
                  Monitoring alerts automatically: a warning at 30 days, critical at 7 days, and
                  when expired (see Alerts &amp; the calendar).
                </p>
              </>
            )}
          </div>
        </>
      )}

      {integrations.length > 0 && !isMeraki && currentIntegration && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="p-3 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                {currentIntegration.vendor_label || currentIntegration.vendor}
              </span>
              <span className="text-xs text-gray-400">connected via vendor API</span>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {(currentIntegration.capabilities || []).map((c) => (
                <span
                  key={c}
                  className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                >
                  {c.replace(/_/g, " ")}
                </span>
              ))}
              {(currentIntegration.capabilities || []).length === 0 && (
                <span className="text-xs text-gray-400">No capabilities reported.</span>
              )}
            </div>
            <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
              Tabs are driven by what this vendor's API supports. Writes for non-Meraki vendors are
              best-effort and stay disabled until you validate + enable them (toggle above).
            </p>
            {/* A relayed controller's tabs are a snapshot. Show its age and
                who collected it, because rows with no qualification read as
                live and this data can be up to a relay cycle old. */}
            {freshness?.relayed && (
              <div className="mt-2 text-[11px] rounded border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-200 p-2">
                {freshness.fetched_at ? (
                  <>
                    <span className="font-medium">
                      Collected {formatRelative(freshness.fetched_at)}
                      {freshness.collector ? ` by ${freshness.collector}` : ""}.
                    </span>{" "}
                    This controller is on the customer's network, so AeroGauge reads it through
                    an on-site agent rather than directly. Tabs show the last collection, which
                    refreshes about every 15 minutes.
                  </>
                ) : (
                  <>
                    <span className="font-medium">No on-site agent has reported yet.</span> This
                    controller is only reachable from the customer's network, so an AeroGauge
                    agent there has to collect it. Check that a device at this site is online.
                    {freshness.last_sync_error ? ` Last error: ${freshness.last_sync_error}` : ""}
                  </>
                )}
              </div>
            )}
            {/* The credential can read but not write. Saying so here beats a
                404 from a button that looked available. */}
            {ccActions?.writes_enabled && ccActions.writes_possible === false && (
              <div className="mt-2 text-[11px] rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 p-2">
                <span className="font-medium">Editing unavailable. </span>
                {ccActions.writes_blocked_reason ||
                  "The stored credential cannot apply configuration changes."}
              </div>
            )}
            {/* Writes ARE allowed, but the credential looks like the wrong
                kind, so an edit may come back unauthorised. Buttons stay -
                see write_caveat() for why this warns instead of blocking. */}
            {ccActions?.writes_enabled &&
              ccActions.writes_possible !== false &&
              !!ccActions.writes_warning && (
                <div className="mt-2 text-[11px] rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 p-2">
                  <span className="font-medium">Heads up. </span>
                  {ccActions.writes_warning}
                </div>
              )}
          </div>
          {/* capability-driven read tabs */}
          <div className="flex flex-wrap gap-1 px-2 pt-2 border-b border-gray-200 dark:border-gray-700">
            {Object.keys(GEN_TAB_CAP_LOOKUP)
              .filter((t) => (currentIntegration.capabilities || []).includes(GEN_TAB_CAP[t]))
              .map((t) => (
                <button
                  key={t}
                  onClick={() => setGenTab(t)}
                  className={`px-3 py-2 text-sm -mb-px border-b-2 ${
                    genTab === t
                      ? "border-indigo-600 text-indigo-700 dark:text-indigo-300 font-medium"
                      : "border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                  }`}
                >
                  {t}
                </button>
              ))}
          </div>
          {(() => {
            const createAction = actionFor(genTab, "create");
            // Actions with nothing to fill in - restarting the gear. They
            // belong on the tab their target capability owns, not on a row:
            // restarting a firewall is not something you do to one of its
            // DHCP leases.
            const runActions = (
              genWrite ? ccActions?.actions || [] : []
            ).filter((a) => a.kind === "run" && a.target === GEN_TAB_CAP[genTab]);
            if (!createAction && runActions.length === 0) return null;
            return (
              <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex flex-wrap gap-2">
                {createAction && (
                  <button
                    onClick={() =>
                      setEditing({
                        action: createAction,
                        objectId: "",
                        objectLabel: "New on this controller",
                        initial: {},
                      })
                    }
                    className="text-xs px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                  >
                    + {createAction.label}
                  </button>
                )}
                {runActions.map((a) => (
                  <button
                    key={a.action}
                    onClick={() =>
                      setEditing({
                        action: a,
                        objectId: "",
                        objectLabel: currentIntegration?.name || "This controller",
                        initial: {},
                      })
                    }
                    className="text-xs px-2.5 py-1 rounded border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-900/30"
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            );
          })()}
          <div className="overflow-x-auto">
            {genLoading ? (
              <p className="text-sm text-gray-400 text-center py-6">Loading from the controller…</p>
            ) : genRows.length === 0 ? (
              /* An empty table under a "Collected 3 minutes ago" banner reads
                 as "this site has no wireless networks". If the agent never
                 collected this capability, say that instead - the usual cause
                 is a credential that can reach some of the controller's API
                 and not the rest. */
              freshness?.relayed &&
              !freshness.capabilities?.[GEN_TAB_CAP_LOOKUP[genTab]] ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6 px-6">
                  The on-site agent's last collection did not include{" "}
                  {genTab.toLowerCase()}, so there is nothing to show here yet — this is not
                  the same as the controller having none. The usual cause is a credential that
                  can read part of the controller's API but not this part; check the
                  integration's username and password.
                </p>
              ) : (
                <p className="text-sm text-gray-400 text-center py-6">
                  Nothing returned for {genTab.toLowerCase()}.
                </p>
              )
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    {(GEN_COLUMNS[genTab] || []).map((c) => (
                      <SortableTh
                        key={c.key}
                        label={c.label}
                        sortKey={c.key}
                        sort={genSort.sort}
                        onSort={genSort.toggle}
                      />
                    ))}
                    {genWrite && <th className={th}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedGenRows.map((row, i) => (
                    <tr key={i} className={rowCls}>
                      {(GEN_COLUMNS[genTab] || []).map((c) => (
                        <td key={c.key} className={c.mono ? `${td} font-mono text-xs` : td}>
                          {c.render ? c.render(row) : String(row[c.key] ?? "")}
                        </td>
                      ))}
                      {genWrite && (
                        <td className={td}>
                          {/* The two quick toggles are only offered where the
                              controller says it can carry them - see
                              quick_methods. On a firewall the Devices rows
                              are DHCP leases, and a Reboot button there
                              offered to restart the whole appliance from a
                              line describing somebody's printer. */}
                          {genTab === "Devices" &&
                          (ccActions?.quick_methods || []).includes("reboot_device") ? (
                            <button
                              onClick={() =>
                                ccPropose(
                                  "reboot_device",
                                  { mac: row.mac_address },
                                  "Reboot device",
                                )
                              }
                              className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                            >
                              Reboot
                            </button>
                          ) : genTab === "Wireless" &&
                            (ccActions?.quick_methods || []).includes("set_ssid") ? (
                            <button
                              onClick={() =>
                                ccPropose(
                                  "set_ssid",
                                  { ssid_id: row.id, fields: { enabled: !row.enabled } },
                                  (row.enabled ? "Disable" : "Enable") + " SSID",
                                )
                              }
                              className="text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                            >
                              {row.enabled ? "Disable" : "Enable"}
                            </button>
                          ) : null}
                          {/* The one-click toggle above stays: disabling a
                              guest network in a hurry should not need a form.
                              Edit is for everything else. */}
                          {/* Edit whatever this vendor says it can change on
                              this tab. `edit` is the controller's own current
                              values (secrets excluded), so the form opens
                              pre-filled and correct without this file knowing
                              a single vendor field name. */}
                          {(() => {
                            const upd = actionFor(genTab, "update");
                            const oid = String(row.object_id ?? "");
                            if (!upd || !oid) return null;
                            return (
                              <button
                                onClick={() =>
                                  setEditing({
                                    action: upd,
                                    objectId: oid,
                                    objectLabel: String(
                                      row.name ?? row.hostname ?? row.description ?? oid,
                                    ),
                                    initial:
                                      (row.edit as Record<string, unknown> | undefined) || {},
                                  })
                                }
                                className="ml-1 text-xs px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
                              >
                                Edit
                              </button>
                            );
                          })()}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {editing && niId && (
        <ControllerActionModal
          niId={niId}
          action={editing.action}
          objectId={editing.objectId}
          objectLabel={editing.objectLabel}
          initial={editing.initial}
          refOptions={refOptions}
          relayed={!!ccActions?.relayed}
          onClose={() => setEditing(null)}
          // Re-read from the controller rather than patching local state: a
          // merge-and-PUT can change more than was sent, and showing our
          // guess instead of the controller's truth is how a UI starts lying
          // about a device's configuration. vNonce is the existing
          // refetch-to-validate trigger.
          onDone={() => setVNonce((n) => n + 1)}
        />
      )}

      {modalAction && (
        <ActionModal
          action={modalAction}
          onClose={() => setModalAction(null)}
          onResult={(u) => {
            patchPending(modalAction.id, u);
            setModalAction((m) => (m ? { ...m, ...u } : m));
            if (u.status === "succeeded") {
              setVNonce((k) => k + 1); // refetch current tab so the change is visible = validation
              if (modalAction.action_type === "meraki_set_client_policy") {
                const cid = String(modalAction.params.client_id || "");
                const pol = String(modalAction.params.device_policy || "Normal");
                if (cid) setClientPolicies((prev) => ({ ...prev, [cid]: pol }));
              }
            }
          }}
        />
      )}
    </div>
  );
}

/** Modal approve/reject prompt shown immediately when a hub button proposes a
 *  change — so you don't have to scroll to the pending strip. Keeps the same
 *  approve-and-execute / reject pipeline; HIGH-risk needs a note. */
function ActionModal({
  action,
  onClose,
  onResult,
}: {
  action: ProposedAction;
  onClose: () => void;
  onResult: (u: Partial<ProposedAction>) => void;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const high = action.risk_tier === "high";

  const approve = async () => {
    if (high && !note.trim()) {
      setError("A note is required for high-risk changes.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await api.post(`/api/v1/ai/actions/${action.id}/approve-and-execute`, { note });
      onResult({ status: r.data.status });
      if (r.data.status === "succeeded") {
        setResult("✓ Applied — Meraki accepted the change. The view below refreshes to confirm.");
      } else {
        // e.g. status "failed" with a stored reason
        setError(r.data.error_message || `Did not apply (status: ${r.data.status}).`);
      }
    } catch (e: any) {
      // execute failures return the reason in detail (e.g. "Meraki PUT failed: HTTP 422 …")
      setError(e.response?.data?.detail || e.message || "Failed to run the change");
    } finally {
      setBusy(false);
    }
  };
  const reject = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post(`/api/v1/ai/actions/${action.id}/reject`, {});
      onResult({ status: r.data.status });
      onClose();
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            Approve this Meraki change?
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            ✕
          </button>
        </div>
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[9px] uppercase font-bold px-1 py-0.5 rounded bg-amber-200 dark:bg-amber-900/50 text-amber-900 dark:text-amber-200">
            {action.risk_tier} risk
          </span>
          <code className="text-[11px] bg-gray-100 dark:bg-gray-900/60 dark:text-gray-100 px-1 rounded">
            {action.action_type}
          </code>
        </div>
        <pre className="text-[11px] bg-gray-50 dark:bg-gray-900/50 rounded p-2 overflow-auto text-gray-700 dark:text-gray-200 max-h-40">
          {JSON.stringify(action.params, null, 2)}
        </pre>
        {high && !result && (
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Approval note (required for high-risk)…"
            className="mt-2 w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded p-2 text-sm h-16"
          />
        )}
        {error && (
          <div className="mt-2 bg-red-50 dark:bg-red-900/25 border border-red-200 dark:border-red-800/60 rounded-lg p-2">
            <div className="text-xs font-semibold text-red-700 dark:text-red-300">
              ✗ It did not run
            </div>
            <div className="text-xs text-red-700 dark:text-red-300 mt-0.5 break-words">{error}</div>
          </div>
        )}
        {result ? (
          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="text-sm text-green-700 dark:text-green-400">{result}</span>
            <button
              onClick={onClose}
              className="text-sm px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg shrink-0"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-2 justify-end">
            <button
              onClick={reject}
              disabled={busy}
              className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
            >
              Reject
            </button>
            <button
              onClick={approve}
              disabled={busy}
              className="text-sm px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium disabled:opacity-50"
            >
              {busy ? "Running…" : "▶ Approve & run"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Approve/reject card for a Meraki change proposed from a hub button.
 *  Nothing runs until approved; HIGH-risk requires an approval note. Uses the
 *  same approve-and-execute / reject pipeline as the AI-proposed actions. */
function ProposedActionCard({
  action,
  onChange,
}: {
  action: ProposedAction;
  onChange: (u: Partial<ProposedAction>) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const approve = async () => {
    let note = "";
    if (action.risk_tier === "high") {
      const n = window.prompt(
        `Approve and run this change?\n\n${action.action_type}\n${JSON.stringify(action.params, null, 2)}\n\nEnter an approval note (required):`,
        "",
      );
      if (!n) return;
      note = n;
    } else if (!window.confirm(`Run this change?\n\n${action.action_type}`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await api.post(`/api/v1/ai/actions/${action.id}/approve-and-execute`, { note });
      onChange({ status: r.data.status });
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/api/v1/ai/actions/${action.id}/reject`, {});
      onChange({ status: r.data.status });
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const done =
    action.status === "succeeded"
      ? { txt: "✓ applied", cls: "text-green-700 dark:text-green-400" }
      : action.status === "failed"
        ? { txt: "✗ failed", cls: "text-red-700 dark:text-red-400" }
        : action.status === "rejected"
          ? { txt: "rejected", cls: "text-gray-500 italic" }
          : null;

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 rounded-lg p-2 text-xs">
      <div className="flex items-center gap-1.5 flex-wrap mb-1">
        <span className="text-[9px] uppercase font-bold px-1 py-0.5 rounded bg-amber-200 dark:bg-amber-900/50 text-amber-900 dark:text-amber-200">
          {action.risk_tier} risk
        </span>
        <code className="text-[10px] bg-white dark:bg-gray-900/60 dark:text-gray-100 px-1 rounded">
          {action.action_type}
        </code>
      </div>
      <pre className="text-[10px] bg-white/70 dark:bg-gray-900/50 rounded p-1 overflow-auto text-gray-700 dark:text-gray-200">
        {JSON.stringify(action.params, null, 2)}
      </pre>
      {done ? (
        <div className={`mt-1.5 font-mono ${done.cls}`}>{done.txt}</div>
      ) : (
        <div className="mt-1.5 flex items-center gap-2">
          <button
            onClick={approve}
            disabled={busy}
            className="text-[11px] px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold disabled:opacity-50"
          >
            {busy ? "running…" : "▶ Approve & run"}
          </button>
          <button
            onClick={reject}
            disabled={busy}
            className="text-[11px] px-2 py-0.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded"
          >
            Reject
          </button>
          <a
            href="/ai-actions"
            className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline ml-auto"
          >
            #{action.id}
          </a>
        </div>
      )}
      {error && <div className="text-[10px] text-red-700 dark:text-red-400 mt-1">{error}</div>}
    </div>
  );
}

/** Hover a row's 💡 to get AI suggestions for THAT item (fetched once, cached).
 *  Advisory only — the tech runs any of them via the ✨ AI Assist / hub buttons. */
function HoverSuggest({
  niId,
  kind,
  item,
  networkId,
  onProposed,
}: {
  niId: number | null;
  kind: string;
  item: Record<string, unknown>;
  networkId?: string;
  onProposed: (actions: ProposedAction[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sugs, setSugs] = useState<string[] | null>(null);
  const [note, setNote] = useState("");
  const [acting, setActing] = useState(false);

  // Click a suggestion → turn it into an approval-gated action + pop the modal.
  const runSuggestion = async (text: string) => {
    if (niId == null || acting) return;
    setActing(true);
    setNote("");
    try {
      const r = await api.post(`/api/v1/meraki/${niId}/act`, {
        instruction: text,
        network_id: networkId,
        item,
      });
      const acts = (r.data?.actions || []) as ProposedAction[];
      if (acts.length) {
        setOpen(false);
        onProposed(acts);
      } else {
        setNote(r.data?.text || "No concrete action for that — try rephrasing.");
      }
    } catch (e: any) {
      setNote(e.response?.data?.detail || "Couldn't create the action (is Action AI enabled?)");
    } finally {
      setActing(false);
    }
  };

  const fetchSugs = async () => {
    if (niId == null || sugs !== null || loading) return;
    setLoading(true);
    setNote("");
    try {
      const r = await api.post(`/api/v1/meraki/${niId}/suggest`, {
        kind,
        item,
        network_id: networkId,
      });
      if (r.data?.enabled === false) setNote("AI is turned off.");
      setSugs(r.data?.suggestions || []);
    } catch (e: any) {
      setNote(e.response?.data?.detail || "AI unavailable");
      setSugs([]);
    } finally {
      setLoading(false);
    }
  };

  // Click to toggle (reliable on touch / small screens — no hover). Fetch on open.
  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      if (next) fetchSugs();
      return next;
    });
  };

  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={toggle}
        title="AI suggestions for this item"
        className={`text-xs px-1.5 py-0.5 rounded border border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/40 ${open ? "bg-violet-100 dark:bg-violet-900/50" : ""}`}
      >
        💡
      </button>
      {open && (
        <>
          {/* click-away layer so the popover closes reliably on any screen */}
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 right-0 mt-1 w-64 max-w-[80vw] bg-white dark:bg-gray-800 border border-violet-200 dark:border-violet-800/60 rounded-lg shadow-lg p-2 text-xs text-left">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-violet-700 dark:text-violet-300">
                ✨ AI suggestions
              </span>
              <button
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
          {loading && <div className="text-gray-400 italic">Thinking…</div>}
          {!loading && note && <div className="text-gray-500 dark:text-gray-400">{note}</div>}
          {!loading && !note && sugs && sugs.length === 0 && (
            <div className="text-gray-500 dark:text-gray-400">No suggestions.</div>
          )}
          {!loading && sugs && sugs.length > 0 && (
            <ul className="space-y-1">
              {sugs.map((s, i) => (
                <li key={i}>
                  <button
                    onClick={() => runSuggestion(s)}
                    disabled={acting}
                    className="text-left w-full text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded px-1 py-0.5 disabled:opacity-50"
                  >
                    ▸ {s}
                  </button>
                </li>
              ))}
            </ul>
          )}
            <div className="mt-1 text-[10px] text-gray-400">
              {acting
                ? "Preparing the change…"
                : "Click a suggestion to propose it (you approve before it runs)."}
            </div>
          </div>
        </>
      )}
    </span>
  );
}
